import moment from 'moment'
import { createSelector } from 'reselect'

import { getCheapestPropertyRatePreferringBedbank } from 'analytics/metasearchPriceAccuracyTracking'
import type { BnblTotalPackagesPrices } from 'components/OfferPage/BnblModal/utils'
import { DEFAULT_OCCUPANCY_FOR_BNBL } from 'components/OfferPage/BnblModal/utils'
import type { OfferDurationOption } from 'components/OfferPage/Filters/DurationTabs/OfferDurationTabs'
import type {
  RoomPackageOptions,
  RoomRateViews,
} from 'components/OfferPage/Property/singlePropertyUtils'
import {
  filterRoomRates,
  getRatesByRoom,
  getRoomsMappings,
} from 'components/OfferPage/Property/singlePropertyUtils'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import type { OfferPageQuickFilters, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { generateOfferPageRoom, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import {
  EmptyArray,
  arrayToMap,
  arrayToObject,
  countWhere,
  fillArray,
  groupBy,
  isNonEmptyArray,
  min,
  nonNullable,
  sortBy,
  trimArray,
  unique,
} from 'lib/array/arrayUtils'
import { allRoomsOccupantsFitRoomCapacity } from 'lib/checkout/cartReservationUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { addDays, addMonths, beginningOfToday, formatDynamicDateRange, formatIsoDate, isAccommodationCheckInDatePast } from 'lib/datetime/dateUtils'
import { getFlightPriceKey as buildFlightPriceKey } from 'lib/flights/flightUtils'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import getPropertyRatesReduxKeys from 'lib/offer/getPropertyRatesReduxKeys'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import { countOccupants, countOccupantsForFlights, formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isFlashHotelOffer, isVillaOffer } from 'lib/offer/offerTypes'
import { getDurationLabel } from 'lib/offer/offerUtils'
import { determineMemberPricing, maybeWithValue, onlyNonZeroFees } from 'lib/offer/pricing'
import { isLeCollectRate, isPromotionRate, isPropertyCollectRate } from 'lib/offer/propertyTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { filterPackageViewsBySoloCapacity, hasSelectedRooms, hasSoloCapacity } from 'lib/offer/offerPageUtil'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getIsMultiRoomEnabled, shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'

import { getBundledPackages } from './bundledOfferPageSelectors'
import mapPackageView from './mapPackageView'
import { isMultipleRoomBookingActive } from './offerPageSharedSelectors'

export const getRelevantPackages = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => getBundledPackages(state),
  (packageViews, duration, bundlePackages) => {
    if (bundlePackages) {
      const packagesData = Object.values(bundlePackages).flat()
      const allAllowPackages = packagesData.map(pkg => pkg.uniqueKey)

      return packageViews.filter(view => allAllowPackages.includes(view.package.uniqueKey) && view.package.duration === duration && view.capacityValid)
    }

    return packageViews.filter(view => view.package.duration === duration && view.capacityValid)
  },
)

function getSellableRoomRateIds(offer: App.Offer, packageViews: Array<App.OfferPackageOptionView>): Set<string> | null {
  const unpackagedViews = packageViews.filter(view => !view.package.roomRate?.isPackaged)

  if (!offer.isDomestic) {
    // 'packaged' views are views that are **always** bought with flights as well
    // we get better margins on packages that are 'packaged' with flights.
    // We assume any international purchase *must* have flights, so swap out our non-packaged rates
    // with packaged rates where possible if it's international
    const packagedViews = packageViews.filter(view => view.package.roomRate?.isPackaged)
    for (const view of packagedViews) {
      if (view.hotelPrice && view.hotelAvailable) {
        const indexPackageViewByDuration = unpackagedViews.findIndex(x => x.package.roomType?.id === view.package.roomType?.id && x.package.roomRate?.packagedRatePlanId === view.package.roomRate?.ratePlanId)
        if (indexPackageViewByDuration >= 0) {
          unpackagedViews[indexPackageViewByDuration] = view
        } else {
          unpackagedViews.push(view)
        }
      }
    }
  }

  const ids = unpackagedViews.map(x => x.package.roomRate?.id).filter(Boolean)

  return ids.length > 0 ? new Set(ids) : null
}

export const getEffectiveCharterBundleRoute = createSelector(
  (state: OfferPageState) => state.charterBundleRoute,
  (state: OfferPageState) => state.charterBundleEnabled,
  (charterBundleRoute, charterBundleEnabled) => (charterBundleEnabled ? charterBundleRoute : undefined),
)

export const getEffectiveBundledWithFlightsOnly = createSelector(
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.charterBundleRoute,
  (state: OfferPageState) => state.charterBundleEnabled,
  (offer, charterBundleRoute, charterBundleEnabled) => {
    if (charterBundleRoute && !charterBundleEnabled) {
      return false
    }
    return !!offer?.bundledWithFlightsOnly || !!(charterBundleRoute && charterBundleEnabled)
  },
)

/**
 * Retrives the room rate ids that we are able to sell in the calendar
 */
export const getRelevantRoomRateIds = createSelector(
  (_state: App.State, offerState: OfferPageState) => offerState.offer!,
  (state: App.State, offerState: OfferPageState) => getRelevantPackages(offerState, state),
  getSellableRoomRateIds,
)

export const getRelevantPackageCalendars = createSelector(
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState, appState: App.State) => getRelevantPackages(state, appState),
  (calendars, packageViews) => {
    return packageViews.map(p => calendars[p.package.uniqueKey]).filter(Boolean)
  },
)

export const getRelevantMonths = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantPackageCalendars(offerState, state),
  (calendars) => {
    return calendars.flatMap(c => c.months)
  },
)

export const getRelevantDays = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantMonths(state, offerState),
  (relevantMonths) => {
    return relevantMonths.flatMap(c => c.days)
  },
)

/**
 * This selector returns all the package views without any consideration for the currently
 * selected check in date.
 */
export const getBasePackageViews = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.airport,
  (_state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (_state: OfferPageState, appState: App.State) => getCanViewLuxPlusBenefits(appState),
  (offer, rooms = initialOfferPageRooms, originAirport, calendars, canViewLuxPlusBenefits): Array<App.OfferPackageOptionView> => {
    let packages = offer.packages
    if (offer.packages.length === 0 && offer.lowestPricePackage) {
      packages = [offer.lowestPricePackage]
    }

    return packages.map((pkg) => mapPackageView(offer, pkg, rooms, {
      airport: originAirport,
      calendar: calendars[pkg.uniqueKey],
      luxPlusMember: canViewLuxPlusBenefits,
    }))
  })

export const getCurrentAvailableRates = createSelector(
  (state: OfferPageState, appState: App.State) => appState.offer.offerAvailableRates[state.offer?.id ?? ''],
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.checkInDate,
  (rates, rooms = initialOfferPageRooms, duration, checkIn): App.OfferAvailableRates => {
    if (checkIn) {
      const key = buildAvailableRateKey(checkIn, moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT), rooms)
      const rateInfo = rates?.[key]
      if (rateInfo) {
        return rateInfo
      }
    }

    return {
      fetching: true,
      error: undefined,
      key: '',
      rates: [],
    }
  })

const emptyRate: App.OfferAvailableRates = {
  fetching: true,
  error: undefined,
  key: '',
  rates: [],
}

const getCurrentMultiRoomRates = createSelector(
  (state: OfferPageState, appState: App.State) => appState.offer.offerAvailableRates[state.offer!.id],
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.checkInDate,
  (rates, rooms = initialOfferPageRooms, duration, checkIn): Array<App.OfferAvailableRates> => {
    return rooms.map(room => {
      if (checkIn) {
        const key = buildAvailableRateKey(checkIn, moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT), [room])
        const rateInfo = rates?.[key]
        if (rateInfo) {
          return rateInfo
        } else {
          return emptyRate
        }
      }

      return emptyRate
    })
  })

export const getPackageViews = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.airport,
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.checkInDate,
  (_state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (_state: OfferPageState, appState: App.State) => getCanViewLuxPlusBenefits(appState),
  getCurrentAvailableRates,
  (offer, rooms = initialOfferPageRooms, airport, packageViews, checkIn, calendars, canViewLuxPlusBenefits, offerRates): Array<App.OfferPackageOptionView> => {
    if (checkIn) {
      const ratesByPackageKey = arrayToMap(offerRates.rates ?? [], rate => rate.packageUniqueKey)
      return packageViews.map(view => mapPackageView(offer, view.package, rooms, {
        calendar: calendars[view.package.uniqueKey],
        checkIn,
        luxPlusMember: canViewLuxPlusBenefits,
        airport,
        rate: ratesByPackageKey.get(view.package.uniqueKey),
      }))
    }
    return packageViews
  },
)

/**
 * This selector returns all the package views without any consideration for the currently
 * selected check in date (i.e. the calendar). It also only returns the pricing for 1 room.
 * See function, getBnblPackageViewByNumberOfRooms to get the pricing for multiple rooms.
 */
export const getBnblPackageViews = createSelector(
  (state: OfferPageState) => state.offer! as App.HotelOffer,
  (offer): Array<App.OfferPackageOptionView> => {
    let packages = offer.packages
    if (offer.packages.length === 0 && offer?.lowestPricePackage) {
      packages = [offer.lowestPricePackage]
    }

    const occupancies: Array<App.Occupants> = [DEFAULT_OCCUPANCY_FOR_BNBL]

    const offerAvailable = scheduleIsCurrent(offer.onlinePurchaseSchedule)
    return packages.map((pkg): App.OfferPackageOptionView => {
      const baseIsAvailable = offerAvailable && !!pkg.price
      const basePrice = pkg.price
      const baseMemberPrice = pkg.memberPrice
      const baseValue = pkg.value
      const taxesAndFees = pkg.taxesAndFees
      const propertyFees = pkg.propertyFees
      const hotelAvailable = baseIsAvailable
      const baseBundledValue = baseValue
      const baseBundledPrice = basePrice
      const hotelDiscountPercent = getValueOffPercent(baseValue, baseMemberPrice || basePrice)
      const bundledDiscountPercent = getValueOffPercent(baseBundledValue, baseBundledPrice)
      const hotelPrice = basePrice
      const hotelTotal = hotelPrice + propertyFees
      const hotelMemberPrice = baseMemberPrice
      const hotelMemberTotal = hotelMemberPrice + propertyFees
      const hotelValue = baseValue
      const pricing: App.PricingWithValue = {
        price: hotelTotal,
        value: hotelValue,
        discountPercent: getValueOffPercent(hotelValue, hotelTotal),
        fees: onlyNonZeroFees([
          { type: 'taxesAndFees', amount: taxesAndFees },
          { type: 'property', amount: propertyFees },
        ]),
        saleUnit: offer.saleUnit,
      }

      return {
        package: pkg,
        hotelPrice,
        hotelValue,
        hotelTotal,
        taxesAndFees,
        propertyFees,
        basePrice,
        baseValue,
        surcharge: 0,
        extraGuestsSurcharge: 0, // bnbl always assumes cost for just 1 adult, it does not take into consideration extra guests
        flightPrice: 0,
        flightsAvailable: false,
        capacityValid: !occupancies || occupancies.every(room => allRoomsOccupantsFitRoomCapacity(room, pkg.roomRate?.capacities ?? [], offer)),
        hotelAvailable,
        hotelDiscountPercent,
        bundledDiscountPercent,
        fromCalendarInfo: false,
        baseMemberPrice,
        hotelMemberPrice,
        hotelMemberTotal: hotelMemberPrice ? hotelMemberPrice + propertyFees : 0,
        hotelMemberValue: hotelMemberPrice ? pkg.memberValue : 0,
        pricing,
        memberPricing: determineMemberPricing(pricing, hotelMemberTotal, pkg.memberValue),
        refundable: true,
      }
    })
  },
)

/**
 * This selector returns the total prices for all the package views inside bnblModal
 * that will be displayed on BNBL modals footer.
 */
export const getBnblPackageViewsTotalPrice = createSelector(
  (state: OfferPageState) => state.offer,
  (state: OfferPageState, appState: App.State) => appState.offer.bnblModal,
  (offer, bnblModal): BnblTotalPackagesPrices => {
    const bnblModalPackages = bnblModal[offer?.id ?? '']
    const totalRoomsPrice = bnblModalPackages.reduce((acc, modalPackage) => {
      const { hotelPrice, hotelValue, package: pkg, propertyFees, hotelMemberPrice } = modalPackage.packageView
      const priceFrom = hotelPrice + propertyFees
      const memberPriceFrom = hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0
      const shouldDisplayValue = !acc.shouldDisplayValue ? acc.shouldDisplayValue : pkg.shouldDisplayValue

      return {
        hotelPrice: acc.hotelPrice + hotelPrice,
        hotelMemberPrice: acc.hotelMemberPrice + hotelMemberPrice,
        priceFrom: acc.priceFrom + priceFrom,
        memberPriceFrom: acc.memberPriceFrom + memberPriceFrom,
        hotelValue: acc.hotelValue + hotelValue,
        shouldDisplayValue,
      }
    }, { hotelPrice: 0, priceFrom: 0, hotelValue: 0, shouldDisplayValue: true, hotelMemberPrice: 0, memberPriceFrom: 0 })

    return totalRoomsPrice
  },
)

/**
 * Retrieves all options for every package (room/rate/group combination)
 *
 * TODO: Bake in multi room support here
 */
export const getDurationPackageOptions = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.rooms,
  (_state: OfferPageState, appState: App.State) => appState.router.location.query.utm_campaign,
  (state: OfferPageState) => pageRequiresRates(state),
  (offer, packageViews, duration, rooms, utmCampaign, requiresRates): Array<App.OfferPackageOptionView> => {
    const chainId = offer.property?.chainId ?? ''
    const usePackagedRates = !offer.isDomestic || isEnabledForFeature(config.PRIORITISE_PACKAGED_RATES_CHAINS_LIST, chainId)

    const viewsByDuration = packageViews.filter(view =>
      view.package.duration === duration &&
      !view.package.roomRate?.isPackaged,
    )

    if (usePackagedRates) {
      /**
       * This section deals with "Packaged" rates
       *
       * "Packaged" (rates that assume flights are included) rates are usually higher margin than normal rates
       * When the offer is not domestic, we assume the user is buying flights with their hotel
       * With this assumption, we then go see if we have a packaged rate we can swap in place for the existing one
       */
      packageViews.filter(view => {
        const available = view.package.duration === duration && view.package.roomRate?.isPackaged && view.hotelAvailable
        if (requiresRates) {
          // dynamic packaged offers *must* have their rates loaded to be considered 'available'
          return available && view.hasAvailableRates
        }
        return available
      }).forEach(view => {
        const indexPackageViewByDuration = viewsByDuration.findIndex(durationView =>
          durationView.package.roomType?.id === view.package.roomType?.id &&
          durationView.package.roomRate?.packagedRatePlanId === view.package.roomRate?.ratePlanId,
        )
        if (indexPackageViewByDuration >= 0) {
          viewsByDuration[indexPackageViewByDuration] = view
        } else {
          viewsByDuration.push(view)
        }
      })
    }

    /**
     * This section deals with Room Groups
     * Rooms can come in groups, i.e. there are many 'rooms' values for the same room and
     * we only want to show the most relevant one
     *
     * To determine the one room that we want to show for a group, we pick the one that is both
     * the cheapest and available.
     *
     * When none are available, we still want to show something - falls back to just the first room
     */
    const groupedPackageViewsByRoomTypeGroup = groupBy(
      viewsByDuration.filter(view => view.package.roomType?.id && view.package.roomType?.group?.id),
      view => view.package.roomType?.group?.id,
    )
    const groupedRoomViews = Array.from(groupedPackageViewsByRoomTypeGroup.values()).flatMap((views) => {
      const cheapestRoomPackage = min(views.filter(view =>
        view.capacityValid &&
        view.hotelPrice &&
        view.hotelAvailable &&
        (!requiresRates || view.hasAvailableRates),
      ), view => view.hotelPrice) ?? views[0]

      return views.filter(view => view.package.roomType?.id == cheapestRoomPackage.package.roomType?.id)
    })
    const groupedRoomViewsWithoutRates = groupedRoomViews.filter(view => !view.package.roomRate?.group?.id)

    /**
     * This section deals with Rate Groups
     * Rates can come in groups, i.e. there are many rates but we only ever want to show
     * one option for this group of rates.
     *
     * To determine the one rate in a group we want to show, we pick the cheapest available rate
     * and use that one.
     *
     * When none are available, we still want to show something - fallback to just the lowest price
     *
     * We need to check 2 groups:
     *  1/ options that have only Rate Groups and do NOT have Room Group
     *  2/ options that previously were grouped by Room Group but have Rate Group at the same time
     */
    const groupedRoomViewsWithRates = groupedRoomViews.filter(view => view.package.roomRate?.group?.id)
    const groupedRatesWithoutRooms = viewsByDuration.filter(view => view.package.roomRate?.group?.id && !view.package.roomType?.group?.id)

    let groupedRateViews: Array<App.OfferPackageOptionView> = [...groupedRoomViewsWithRates, ...groupedRatesWithoutRooms]
    if (utmCampaign !== 'google-hotel-ads') {
      const groupedPackageViews = groupBy(
        groupedRateViews,
        view => `${view.package.roomType?.id}-${view.package.roomRate?.group?.id}`,
      )

      groupedRateViews = Array.from(groupedPackageViews.values()).map(views => {
        const cheapestPackage = min(views.filter(view => view.capacityValid && view.hotelPrice), view => view.hotelPrice) ?? views[0]
        const availablePackages = views.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
        const cheapestAvailablePackage = min(availablePackages, view => view.hotelPrice)
        return cheapestAvailablePackage ?? cheapestPackage
      })
    }

    const ungroupedViews = viewsByDuration.filter(view => !view.package.roomRate?.group?.id && !view.package.roomType?.group?.id)
    /**
     * Combine the list options which contains:
     * - options that do not have either Room or Rate group
     * - options that grouped by Room Group and do not have options with Rate Group
     * - options that have Rate Group (it can have Room Group or not)
     */
    const allViews = [
      ...ungroupedViews,
      ...groupedRoomViewsWithoutRates,
      ...groupedRateViews,
    ]

    /**
     * Filter packages that support solo occupancy
     */
    return filterPackageViewsBySoloCapacity(allViews, rooms)
  },
)

export const getCheapestRoomOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getDurationPackageOptions(state, appState),
  (packageOptions) => {
    const validCapacityPackageOptions = packageOptions.filter(p => p.capacityValid)
    const packagesByRoom = groupBy(validCapacityPackageOptions, p => p.package.roomType?.id)
    return [...packagesByRoom.values()].map(options => min(options, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER))
  },
)

/**
 * Package views for tier progression calculation on Flash offer page.
 * - When date is selected: uses packages for selected duration (svc_calendar is source of truth)
 * - When no date selected: uses packages up to 9 days duration
 */
const MAX_TIER_PROGRESSION_DURATION = 9

export const getTierProgressionPackageViews = createSelector(
  getBasePackageViews,
  getDurationPackageOptions,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.duration,
  (basePackageViews, durationPackageViews, checkInDate, duration) => {
    // Date selected: use selected duration (svc_calendar is source of truth)
    if (checkInDate) {
      return durationPackageViews
    }
    // Selected duration exceeds tier progression cap: filter to that exact duration
    if (duration > MAX_TIER_PROGRESSION_DURATION) {
      return basePackageViews.filter(view => view.package.duration === duration)
    }
    // No date, short duration: use packages up to 9 days
    return basePackageViews.filter(view => view.package.duration <= MAX_TIER_PROGRESSION_DURATION)
  },
)

/**
 * Retrieves cheapest option for each unique room
 * Similar to get package options above but at a room level
 */
export const getPropertyRoomOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (packageViews, duration): Array<App.OfferPackageOptionView> => {
    const applicableViews = packageViews.filter(view => view.package.duration === duration)
    const viewsByRoomId = groupBy(applicableViews, view => view.package.roomType?.id)

    return [...viewsByRoomId.values()].map(views => {
      const availableViews = views.filter(view => view.capacityValid && view.hotelAvailable)
      return min(availableViews, views => views.hotelPrice) ?? views[0]
    })
  },
)

export const getAvailablePackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (packageOptions): Array<App.OfferPackageOptionView> => packageOptions.filter(option => option.hotelAvailable && option.capacityValid),
)

const pageRequiresRates = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState) => !!state.checkInDate,
  (offer, hasDates) => {
    return !!offer.property?.useDynamicCancellationPolicies && hasDates
  },
)

export const getAvailableDurationPackageOptions = createSelector(
  getDurationPackageOptions,
  pageRequiresRates,
  isMultipleRoomBookingActive,
  (_: OfferPageState, appState: App.State) => appState.system.previewMode,
  (options, requiresRates, isMultiRoom, previewMode) => {
    if (previewMode) return options
    return options.filter(option => {
      let available = option.hotelAvailable && option.capacityValid
      // our duration package options selector doesn't support multi room rates
      // So when it's multi room, don't require rates as we'll never have them
      // This is safe because this selector is only used for "from" prices
      // actual multi room selectors will take over for the more important values
      if (requiresRates && !isMultiRoom) {
        available = available && !!option.hasAvailableRates
      }
      return available
    })
  },
)

export const getCheapestAvailablePackageOption = createSelector(
  getAvailableDurationPackageOptions,
  getPackageViews,
  (packageOptions, views): App.OfferPackageOptionView => {
    const cheapest = min(packageOptions, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER)
    return cheapest ?? packageOptions[0] ?? views[0]
  },
)

export const getBaseTacticalDurationPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.duration,
  (packageOptions, duration): Array<App.OfferPackageOptionView> => packageOptions.filter(option => (
    option?.package.hasTactical &&
    option.hotelAvailable &&
    option.capacityValid &&
    option.package.duration === duration
  )),
)

export const getTacticalPackageOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getDurationPackageOptions(state, appState),
  (state: OfferPageState, appState: App.State) => getBaseTacticalDurationPackageOptions(state, appState),
  (packageOptions, availablePackageOptions): Array<App.OfferPackageOptionView> => {
    const availableTacticalOptions = packageOptions.filter(option =>
      option?.package.hasTactical && option.capacityValid && option.hotelAvailable,
    )

    const options = availableTacticalOptions.length > 0 ? availableTacticalOptions : availablePackageOptions

    const optionsByRatePlanId = groupBy(options, option => {
      const [commonInclusions, bonusInclusion] = extractPkgInclusions(option.package)

      const allInclusions = [...commonInclusions, ...bonusInclusion]

      if (allInclusions.length > 0) {
        return allInclusions.map(i => i.description).join('|')
      }

      return option.package.roomRate?.ratePlanId
    })

    return sortBy([...optionsByRatePlanId.values()].map(options => {
      return min(options, option => option.hotelPrice ? option.hotelPrice : Number.MAX_SAFE_INTEGER) ?? options[0]
    }), d => d.hotelPrice, 'asc')
  },
)

export const getCheapestFilteredPackageOption = createSelector(
  getDurationPackageOptions,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.flights,
  (packageOptions, duration, flights): App.OfferPackageOptionView => {
    if (flights) {
      const availableOptions = packageOptions.filter(option => option.package.duration === duration && option.flightsAvailable && option.capacityValid)
      return min(availableOptions, option => option.hotelPrice + option.flightPrice) ?? packageOptions[0]
    }
    const availableOptions = packageOptions.filter(option => option.package.duration === duration && option.hotelAvailable && option.capacityValid)
    return min(availableOptions, option => option.hotelPrice) ?? packageOptions[0]
  },
)

export const getCheapestPropertyRoomOption = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyRoomOptions(state, appState),
  (state: OfferPageState) => state.flights,
  (packageOptions, flights): App.OfferPackageOptionView => {
    if (flights) {
      const availableOptions = packageOptions.filter(option => option.flightsAvailable && option.capacityValid)
      return min(availableOptions, option => option.hotelPrice + option.flightPrice) ?? packageOptions[0]
    }
    const availableOptions = packageOptions.filter(option => option.hotelAvailable && option.capacityValid)
    return min(availableOptions, option => option.hotelPrice) ?? packageOptions[0]
  },
)

export const getPackageOptionsByUniqueKey = createSelector(
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (views) => arrayToObject(views, p => p.package.uniqueKey),
)

export const getDurationOptions = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState, appState: App.State) => appState.system.previewMode,
  (state: OfferPageState) => !!state.checkInDate,
  getEffectiveBundledWithFlightsOnly,
  (packageOptions, offer, previewMode, hasCheckInDate, bundledWithFlightsOnly): Array<OfferDurationOption> => {
    const validCapacityPackageOptions = packageOptions.filter(p => p.capacityValid)
    const packagesViewsByDuration = [...groupBy(validCapacityPackageOptions, p => p.package.duration).entries()]
    const options = packagesViewsByDuration.map<OfferDurationOption>(([duration, views]) => {
      // Preview is used only by content people.
      // We don't check the hotel availability in this case
      const applicableViews = previewMode ? views : views.filter(view => bundledWithFlightsOnly ? view.flightPrice : view.hotelAvailable)
      const cheapestHotel = min(applicableViews, view => view.hotelPrice ?? Number.MAX_SAFE_INTEGER)

      const hasBonusInclusionsV1 = views.some(view => !!view.package.bonusInclusions.length)
      const hasBonusInclusionsV2 = views.some(view => view.package.inclusions?.some(inclusion => inclusion.isBonus))

      const hasTactical = views.some(view => view.package.hasTactical && view.hotelAvailable)

      const stayPayTier = hasCheckInDate ? views.filter(view => view.hotelAvailable).map(view => {
        return view?.package?.stayPay?.find(tier => tier.stayNights === duration && tier.freeNights > 0)
      }).find(Boolean) : undefined

      return {
        duration,
        lowestPrice: cheapestHotel?.hotelTotal,
        lowestMemberPrice: cheapestHotel?.hotelMemberTotal,
        discountPercent: cheapestHotel?.hotelDiscountPercent,
        hasBonusInclusions: hasBonusInclusionsV1 || hasBonusInclusionsV2,
        hasTactical,
        stayPayTier,
        soldOut: !cheapestHotel?.hotelPrice,
      }
    })
    return sortBy(options, d => d.duration, 'asc')
  },
)

const CHARTER_UNBUNDLED_DURATIONS = new Set([5, 6, 7])

export function filterCharterUnbundledDurationOptions(options: Array<OfferDurationOption>): Array<OfferDurationOption> {
  return options.filter(option => CHARTER_UNBUNDLED_DURATIONS.has(option.duration) && !option.soldOut)
}

export const getCalendarDurationOptionsWithCharterBundleRestrictions = createSelector(
  getDurationOptions,
  (state: OfferPageState) => state.charterBundleRoute,
  (state: OfferPageState) => state.charterBundleEnabled,
  (_state: OfferPageState, appState: App.State) => appState.auth.account.isSpoofed,
  (options, charterBundleRoute, charterBundleEnabled, isSpoofed) => {
    if (charterBundleRoute && !charterBundleEnabled && !isSpoofed) {
      return filterCharterUnbundledDurationOptions(options)
    }
    return options
  },
)

export const getLowestAvailableStayPayTier = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (packageViews) => {
    const availableViews = packageViews.filter(view => view.hotelAvailable)

    const lowestAvailableStayPayTier = availableViews
      .flatMap(view => view.package.stayPay || [])
      .reduce((lowest, tier) => {
        if (!lowest || tier.stayNights < lowest.stayNights) {
          return tier
        }
        return lowest
      }, null as App.StayPayTier | null)

    return lowestAvailableStayPayTier
  },
)

export const getCalendarCheapestDaysFiltered = createSelector(
  (state: App.State, offerState: OfferPageState) => getSelectedPackageView(offerState, state)?.package,
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (_state: App.State, offerState: OfferPageState) => offerState.flights,
  (_state: App.State, offerState: OfferPageState) => offerState.displayMemberPrice,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State, offerState: OfferPageState) => getRelevantRoomRateIds(state, offerState),
  (_state: App.State, offerState: OfferPageState) => getFlashCalendarCutoffDate(offerState.offer),
  (pkg, room, flights, displayMemberPrice, calendars, relevantRoomRateIds, calendarVisibleCutoffDate) => {
    const cheapestDays: { [key: string]: number } = {}
    if (displayMemberPrice) {
      return cheapestDays
    }
    if (calendars) {
      let filteredCalendars = Object.values(calendars).filter(c => relevantRoomRateIds?.has(c.cheapestDay?.roomRateId ?? ''))
      // Hidden (beyond-cutoff) days must not set the "cheapest" price marker shown for visible months/days.
      if (calendarVisibleCutoffDate) {
        filteredCalendars = filteredCalendars.filter(c => !c.cheapestDay || c.cheapestDay.checkIn <= calendarVisibleCutoffDate)
      }
      if (pkg) {
        filteredCalendars = filteredCalendars.filter(c => c.cheapestDay?.uniqueKey === pkg.uniqueKey)
      } else if (room) {
        filteredCalendars = filteredCalendars.filter(c => c.cheapestDay?.roomId === room.id)
      }
      filteredCalendars.forEach((calendar) => {
        if (calendar.uniqueKey) {
          let hotelTotal: number
          if (flights) {
            hotelTotal = (displayMemberPrice ? calendar.cheapestDay?.packagedMemberTotal : calendar.cheapestDay?.packagedTotal) ?? 0
          } else {
            hotelTotal = (displayMemberPrice ? calendar.cheapestDay?.hotelMemberTotal : calendar.cheapestDay?.hotelTotal) ?? 0
          }
          cheapestDays[calendar.uniqueKey] = hotelTotal
        }
      })
    }
    return cheapestDays
  },
)

export const getCalendarCheapestDays = createSelector(
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState) => state.displayMemberPrice,
  (calendars, displayMemberPrice): Record<string, App.CalendarCheapestDay> => {
    const cheapestDays: { [key: string]: App.CalendarCheapestDay } = {}
    if (calendars) {
      Object.values(calendars).forEach((calendar) => {
        if (calendar.uniqueKey) {
          const hotelPrice = (displayMemberPrice ? calendar.cheapestDay?.hotelMemberPrice : calendar.cheapestDay?.hotelPrice) ?? 0
          const hotelTotal = (displayMemberPrice ? calendar.cheapestDay?.hotelMemberTotal : calendar.cheapestDay?.hotelTotal) ?? 0
          const hotelValue = calendar.cheapestDay?.hotelValue ?? 0
          const surcharge = calendar.cheapestDay?.surcharge ?? 0
          cheapestDays[calendar.uniqueKey] = {
            hotelPrice,
            hotelTotal,
            hotelValue,
            surcharge,
            pricing: hotelTotal ? maybeWithValue({
              price: hotelTotal,
              fees: onlyNonZeroFees([
                { type: 'surcharge', amount: surcharge },
              ]),
              saleUnit: 'package',
            }, hotelValue) : undefined,
          }
        }
      })
    }
    return cheapestDays
  },
)

type MonthDayViewMetadata = { year: number, monthIndex: number, tacticalDays: Array<string> }
export type MonthDayView = [string, Array<App.CalendarDay>, MonthDayViewMetadata]

function applyCharterUnbundledDayRestrictions(
  days: Array<App.CalendarDay>,
  duration: number,
  minCheckOut?: string,
  maxCheckOut?: string,
  allowedCheckInWeekdays?: ReadonlySet<number>,
) {
  if (!duration) {
    return days
  }
  const nextDays = days.map(day => {
    let isAllowed = true
    if (allowedCheckInWeekdays && allowedCheckInWeekdays.size > 0) {
      const weekday = moment(day.checkIn).day()
      if (!allowedCheckInWeekdays.has(weekday)) {
        isAllowed = false
      }
    }
    if (minCheckOut && maxCheckOut) {
      const checkOut = moment(day.checkIn).add(duration, 'days')
      if (!(checkOut.isSameOrAfter(minCheckOut, 'day') && checkOut.isSameOrBefore(maxCheckOut, 'day'))) {
        isAllowed = false
      }
    }
    if (!isAllowed) {
      return {
        ...day,
        hotelAvailable: false,
        flightsAvailable: false,
      }
    }
    return day
  })
  return nextDays
}

function getAllowedCharterCheckInWeekdays(duration: number): ReadonlySet<number> | undefined {
  // Maldives charter hotel-only combos should align to arrival (Tuesday) and departure (Sunday).
  if (duration === 5) return new Set([2]) // Tuesday
  if (duration === 6) return new Set([1, 2]) // Monday, Tuesday
  if (duration === 7) return new Set([0, 2]) // Sunday, Tuesday
  return undefined
}

/**
 * Flash offers only show check-in dates up to the last full month a customer could still
 * check out in: last valid checkout is travelToDate + 1 day, rounded up to the end of that
 * month (so the final month is never shown as a single trailing day).
 *
 * This is a display-only boundary for what the calendar renders/selects from — it is not the
 * source of truth for whether a specific check-in date is actually bookable. Real bookability
 * for a given day is governed by that day's own availability data, and check-in dates restored
 * from the URL are validated separately (see initialOfferPageStateSelectors.tsx) against the
 * offer's raw travelToDate/bookByDate. Keep these concerns separate rather than reusing this
 * rounded-to-month-end value as a validity check elsewhere.
 */
function getFlashCalendarCutoffDate(offer: OfferPageState['offer']): string | undefined {
  if (!isFlashHotelOffer(offer) || !offer.travelToDate) {
    return undefined
  }
  const cutoffDate = moment(offer.travelToDate).add(1, 'day').endOf('month').format(ISO_DATE_FORMAT)

  if (isAccommodationCheckInDatePast(cutoffDate, { timeZone: offer.property?.timezone })) {
    return undefined
  }
  return cutoffDate
}

function buildCalendarDays(
  relevantDays: Array<App.CalendarDay>,
  pkg: App.HotelPackage | undefined,
  room: App.PackageRoomType | undefined,
  calendars: App.State['calendar']['calendarsByPackageKey'],
  enableCalendarRecommendations: boolean | undefined,
  relevantRoomRateIds: Set<string> | null,
  displayMemberPrice: boolean | undefined,
  cheapestDays: { [key: string]: number } | undefined,
  charterBundleRoute: App.CharterBundleRoute | undefined,
  charterBundleEnabled: boolean | undefined,
  charterBundleCheckoutRange: OfferPageState['charterBundleCheckoutRange'],
  duration: number,
  // Bounds which days are visible in the calendar grid only — not a check-in validity check.
  // See getFlashCalendarCutoffDate for why these are kept separate.
  calendarVisibleCutoffDate: string | undefined,
): Array<MonthDayView> {
  const maybeMemberPriceMaybePrice = <T>(memberPrice: T, nonMemberPrice: T) => displayMemberPrice ? memberPrice : nonMemberPrice
  let days: Array<App.CalendarDay> = []
  if (pkg) {
    days = relevantDays.filter(day => day.uniqueKey === pkg.uniqueKey)
  } else if (room) {
    days = relevantDays.filter(day => day.roomId === room.id)
  } else {
    days = relevantDays
  }
  if (calendarVisibleCutoffDate) {
    days = days.filter(day => day.checkIn <= calendarVisibleCutoffDate)
  }
  if (charterBundleRoute && !charterBundleEnabled) {
    const allowedWeekdays = getAllowedCharterCheckInWeekdays(duration)
    days = applyCharterUnbundledDayRestrictions(
      days,
      duration,
      charterBundleCheckoutRange?.minCheckOut,
      charterBundleCheckoutRange?.maxCheckOut,
      allowedWeekdays,
    )
  }
  const cheapestDayPrice = cheapestDays ? Object.values(cheapestDays).reduce((prev, curr) => prev < curr ? prev : curr, Number.MAX_SAFE_INTEGER) : null

  // Filtering days by relevant packages
  if (relevantRoomRateIds) {
    days = days.filter(s => relevantRoomRateIds.has(s.roomRateId))
  }

  days = days.map(day => {
    return {
      ...day,
      hotelTotal: maybeMemberPriceMaybePrice(day.hotelMemberTotal, day.hotelTotal),
      packagedTotal: maybeMemberPriceMaybePrice(day.packagedMemberTotal, day.packagedTotal),
      hotelPrice: maybeMemberPriceMaybePrice(day.hotelMemberPrice, day.hotelPrice),
      roomPrices: maybeMemberPriceMaybePrice(day.roomMemberPrices, day.roomPrices),
      cheapest: day.hotelTotal == cheapestDayPrice,
    }
  })
  // We might still be loading some, make sure we have **all** the calendars needed,
  // otherwise we can't accurately show the lowest price for that day
  const allPackageIds = unique(days.map(d => d.uniqueKey))
  if (allPackageIds.every(id => !!calendars[id])) {
    // now find the cheapest price for each day
    const daysByCheckIn = groupBy(days, day => day.checkIn)
    // It's safe to assert that the result of `min` will be defined, because groupBy will only create groups with at least 1 item
    const values = [...daysByCheckIn.values()].map(dayGroup => min(dayGroup, day => day.hotelAvailable ? day.hotelTotal : Number.MAX_SAFE_INTEGER)!)
    const groupedByTactical = groupBy(days.filter(d => d.hasTactical && d.hotelAvailable), day => day.monthKey)
    const groupedEntries: Array<MonthDayView> = Array.from(groupBy(values, day => day.monthKey).entries())
      .map(monthGroup => {
        const firstDate = moment(monthGroup[1][0].checkIn)
        const year = firstDate.year()
        const monthIndex = firstDate.month()
        const tacticalDays = Array.from(new Set((groupedByTactical.get(monthGroup[1][0].monthKey) ?? []).map(x => x.checkIn)).values())
        const metadata = { year, monthIndex, tacticalDays }

        return [monthGroup[0], monthGroup[1], metadata]
      })

    if (enableCalendarRecommendations) {
      // Show unavailable items in this casee
      return groupedEntries
    }
    return trimArray(
      groupedEntries,
      entry => entry[1].every(day => !day.hotelAvailable),
    )
  }

  return []
}

export const getCalendarDays = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (state: App.State, offerState: OfferPageState) => getSelectedPackageView(offerState, state)?.package,
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (_state: App.State, offerState: OfferPageState) => shouldEnableCalendarRecommendations(offerState.offer),
  (state: App.State, offerState: OfferPageState) => getRelevantRoomRateIds(state, offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.displayMemberPrice,
  (state: App.State, offerState: OfferPageState) => getCalendarCheapestDaysFiltered(state, offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleRoute,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleEnabled,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleCheckoutRange,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (_state: App.State, offerState: OfferPageState) => getFlashCalendarCutoffDate(offerState.offer),
  (
    relevantDays,
    pkg,
    room,
    calendars,
    enableCalendarRecommendations,
    relevantRoomRateIds,
    displayMemberPrice,
    cheapestDays,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
    calendarCutoffDate,
  ): Array<MonthDayView> => buildCalendarDays(
    relevantDays,
    pkg,
    room,
    calendars,
    enableCalendarRecommendations,
    relevantRoomRateIds,
    displayMemberPrice,
    cheapestDays,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
    calendarCutoffDate,
  ),
)

const getFixedFlashLengthOfStayRelevantDays = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState) => state.room,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.flights,
  (state: OfferPageState) => state.displayMemberPrice,
  (packageViews, calendars, room, duration, flights, displayMemberPrice): Array<App.CalendarDay> => {
    const dayCandidates = packageViews
      .filter(view => view.capacityValid && (!room || view.package.roomType?.id === room.id))
      .flatMap(view => (calendars[view.package.uniqueKey]?.months.flatMap(month => month.days) ?? EmptyArray).map(day => ({
        day,
        duration: view.package.duration,
      })))

    return Array.from(groupBy(dayCandidates, candidate => candidate.day.checkIn).values()).map(dayGroup => {
      const selectedDurationDays = dayGroup.filter(candidate => candidate.duration === duration)
      const candidates = selectedDurationDays.length ? selectedDurationDays : dayGroup

      return min(candidates, candidate => isCalendarDayAvailable(candidate.day, flights) ? getCalendarDayDisplayTotal(candidate.day, flights, displayMemberPrice) : Number.MAX_SAFE_INTEGER)!.day
    })
  },
)

const getFixedFlashLengthOfStayRelevantRoomRateIds = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.room,
  (offer, packageViews, room) => {
    const filteredPackageViews = packageViews.filter(view => view.capacityValid && (!room || view.package.roomType?.id === room.id))

    return getSellableRoomRateIds(offer, filteredPackageViews)
  },
)

export const getFixedFlashLengthOfStayCalendarDays = createSelector(
  (state: App.State, offerState: OfferPageState) => getFixedFlashLengthOfStayRelevantDays(offerState, state),
  (_state: App.State, _offerState: OfferPageState): App.HotelPackage | undefined => undefined,
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (_state: App.State, offerState: OfferPageState) => shouldEnableCalendarRecommendations(offerState.offer),
  (state: App.State, offerState: OfferPageState) => getFixedFlashLengthOfStayRelevantRoomRateIds(offerState, state),
  (_state: App.State, offerState: OfferPageState) => offerState.displayMemberPrice,
  (_state: App.State, _offerState: OfferPageState): { [key: string]: number } | undefined => undefined,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleRoute,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleEnabled,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleCheckoutRange,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (_state: App.State, offerState: OfferPageState) => getFlashCalendarCutoffDate(offerState.offer),
  (
    relevantDays,
    pkg,
    room,
    calendars,
    enableCalendarRecommendations,
    relevantRoomRateIds,
    displayMemberPrice,
    cheapestDays,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
    calendarCutoffDate,
  ): Array<MonthDayView> => buildCalendarDays(
    relevantDays,
    pkg,
    room,
    calendars,
    enableCalendarRecommendations,
    relevantRoomRateIds,
    displayMemberPrice,
    cheapestDays,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
    calendarCutoffDate,
  ),
)

function getCalendarDayDisplayTotal(day: App.CalendarDay, flights: boolean, displayMemberPrice?: boolean) {
  if (flights) {
    return displayMemberPrice ? day.packagedMemberTotal || day.packagedTotal : day.packagedTotal
  }

  return displayMemberPrice ? day.hotelMemberTotal || day.hotelTotal : day.hotelTotal
}

function isCalendarDayAvailable(day: App.CalendarDay, flights: boolean) {
  return day.hotelAvailable && (!flights || day.flightsAvailable)
}

type FixedFlashDateBoundary = 'checkIn' | 'checkOut'

function getCheapestFixedFlashCalendarDaysByBoundary(
  packageViews: Array<App.OfferPackageOptionView>,
  calendars: App.State['calendar']['calendarsByPackageKey'],
  boundaryDate: string | undefined,
  room: App.PackageRoomType | undefined,
  flights: boolean,
  displayMemberPrice: boolean | undefined,
  matchBoundary: FixedFlashDateBoundary,
  resultBoundary: FixedFlashDateBoundary,
): Record<string, App.CalendarDay> {
  if (!boundaryDate) return {}

  return packageViews
    .filter(view => view.capacityValid && (!room || view.package.roomType?.id === room.id))
    .reduce<Record<string, App.CalendarDay>>((calendarDays, view) => {
      const calendarDay = calendars[view.package.uniqueKey]?.months
        .flatMap(month => month.days)
        .find(day => day[matchBoundary] === boundaryDate)

      if (!calendarDay || !isCalendarDayAvailable(calendarDay, flights)) return calendarDays

      const resultDate = calendarDay[resultBoundary]
      const existingDay = calendarDays[resultDate]
      if (!existingDay || getCalendarDayDisplayTotal(calendarDay, flights, displayMemberPrice) < getCalendarDayDisplayTotal(existingDay, flights, displayMemberPrice)) {
        calendarDays[resultDate] = calendarDay
      }

      return calendarDays
    }, {})
}

export const getFixedFlashLengthOfStayCheckoutCalendarDays = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.room,
  (state: OfferPageState) => state.flights,
  (state: OfferPageState) => state.displayMemberPrice,
  (packageViews, calendars, checkIn, room, flights, displayMemberPrice): Record<string, App.CalendarDay> => (
    getCheapestFixedFlashCalendarDaysByBoundary(packageViews, calendars, checkIn, room, flights, displayMemberPrice, 'checkIn', 'checkOut')
  ),
)

export const getFixedFlashLengthOfStayCheckInCalendarDays = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState, appState: App.State) => appState.calendar.calendarsByPackageKey,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.room,
  (state: OfferPageState) => state.flights,
  (state: OfferPageState) => state.displayMemberPrice,
  (packageViews, calendars, checkOut, room, flights, displayMemberPrice): Record<string, App.CalendarDay> => (
    getCheapestFixedFlashCalendarDaysByBoundary(packageViews, calendars, checkOut, room, flights, displayMemberPrice, 'checkOut', 'checkIn')
  ),
)

export const getFixedFlashLengthOfStayMaximumDuration = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (state: OfferPageState) => state.room,
  (state: OfferPageState) => state.flights,
  (packageViews, room, flights): number | undefined => {
    const durations = packageViews
      .filter(view => view.capacityValid && (!room || view.package.roomType?.id === room.id))
      .filter(view => flights ? view.flightPrice : view.hotelAvailable)
      .map(view => view.package.duration)

    return durations.length ? Math.max(...durations) : undefined
  },
)

const getSoldOutRoomCalendarOfferState = createSelector(
  (_state: App.State, offerState: OfferPageState) => offerState,
  (_state: App.State, _offerState: OfferPageState, roomType: App.PackageRoomType) => roomType,
  (offerState, roomType): OfferPageState => ({
    ...offerState,
    room: roomType,
    selectedPackageKey: undefined,
  }),
)

export const getSoldOutRoomCalendarDays = createSelector(
  (state: App.State) => state,
  (state: App.State, offerState: OfferPageState, roomType: App.PackageRoomType) => getSoldOutRoomCalendarOfferState(state, offerState, roomType),
  (state, soldOutRoomCalendarOfferState): Array<MonthDayView> => getCalendarDays(state, soldOutRoomCalendarOfferState),
)

export const getSelectedOfferPageDay = createSelector(
  (state: App.State, offerState: OfferPageState) => getCalendarDays(state, offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.checkInDate,
  (daysByMonth, checkIn) => {
    if (!checkIn) {
      return
    }
    for (const [, days] of daysByMonth) {
      const day = days.find(d => d.checkIn === checkIn)
      if (day) {
        return day
      }
    }
  },
)

export const getCheapestSelectedPackageOption = createSelector(
  (state: App.State, offerState: OfferPageState) => getSelectedOfferPageDay(state, offerState),
  (state: App.State, offerState: OfferPageState) => getAvailableDurationPackageOptions(offerState, state),
  (selectedDay, packageViews) => {
    if (selectedDay) {
      return packageViews.find(view => view.package.uniqueKey === selectedDay.uniqueKey)
    }
  },
)

export type BaseRatePackageView = Pick<App.HotelPackage, 'duration' | 'value' | 'memberValue' | 'id'>

export const getBaseRatePackagesView = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.offer,
  (offer): Array<BaseRatePackageView> => {
    if (!offer?.packages) {
      return []
    }

    return nonNullable(offer.packages.map((option) => {
      if (option.isBaseRate) {
        return { duration: option.duration, value: option.value, memberValue: option.memberValue, id: option.id }
      }
    }))
  },
)

export const getCheapestSelectedPackageOptionWithDefault = createSelector(
  getCheapestSelectedPackageOption,
  (state: App.State, offerState: OfferPageState) => getCheapestAvailablePackageOption(offerState, state),
  (cheapestSelectedPackage, cheapestAvailablePackage) => {
    return cheapestSelectedPackage ?? cheapestAvailablePackage
  },
)

export const getPackagesForFilters = createSelector(
  (state: App.State, offerState: OfferPageState) => getBasePackageViews(offerState, state),
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (packageViews, room, duration) => {
    return packageViews.filter(view => {
      let filter = true
      if (duration) {
        filter = filter && view.package.duration === duration
      }

      if (room) {
        filter = filter && view.package.roomType?.id === room.id
      }

      return filter
    })
  },
)

interface CalendarPickerMonthLoyaltyConfig {
  availableUpgradeTargetsUniqueKeys: Record<App.LoyaltyStayUpgradeType, string | undefined>
}

export interface CalendarPickerMonthView {
  key: string
  month: string
  year: string
  yearNum: number
  monthIndex: number
  hotelTotal: number
  packagedTotal: number
  isLuxPlusPrice: boolean
  hotelAvailable: boolean
  packagedAvailable: boolean
  limitedAvailability: boolean
  loading?: boolean
  flightCacheActive: boolean
  hasPromo: boolean
  hasTactical: boolean
  hasFlightDeal: boolean
  loyalty: CalendarPickerMonthLoyaltyConfig
  cheapest: boolean
}

export const getFlightDealIdsByYearMonth = createSelector(
  (state: App.State, offerState: OfferPageState) => getRelevantMonths(state, offerState),
  (relevantMonths) => {
    const dealIds = relevantMonths.reduce((resultMap, monthView) => {
      const filteredDays = monthView.days.filter(day => !!day.dealId)
      filteredDays.forEach((day) => {
        if (day.dealId) {
          if (!(day.year in resultMap)) {
            resultMap[day.year] = {}
          }
          resultMap[day.year][day.monthKey] = day.dealId
        }
        return resultMap
      })
      return resultMap
    }, {} as Record<string, Record<string, string>>)
    return {
      dealIds,
    }
  },
)

export const getCalendarMonths = createSelector(
  (state: App.State, offerState: OfferPageState) => getSelectedPackageView(offerState, state)?.package,
  (_state: App.State, offerState: OfferPageState) => offerState.room,
  (_state: App.State, offerState: OfferPageState) => getEffectiveBundledWithFlightsOnly(offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.displayMemberPrice,
  (state: App.State, offerState: OfferPageState) => getRelevantMonths(state, offerState),
  (state: App.State, offerState: OfferPageState) => isFetchingCalendars(state, offerState),
  (state: App.State, offerState: OfferPageState) => shouldEnableCalendarRecommendations(offerState.offer),
  (state: App.State, offerState: OfferPageState) => getCalendarCheapestDaysFiltered(state, offerState),
  (state: App.State, offerState: OfferPageState) => getRelevantRoomRateIds(state, offerState),
  (_state: App.State, offerState: OfferPageState) => getFlashCalendarCutoffDate(offerState.offer),
  (_state: App.State, offerState: OfferPageState) => (
    isFlashHotelOffer(offerState.offer) ? offerState.offer.property?.timezone : undefined
  ),
  (pkg, room, bundledWithFlights, displayMemberPrice, allRelevantMonths, fetching, enableCalendarRecommendations, cheapestDays, relevantRoomRateIds, calendarCutoffDate, propertyTimezone): { months: Array<CalendarPickerMonthView>, availableCount: number } => {
    // Flash offers only show months up to the last full month a customer could still check out in
    const relevantMonths = calendarCutoffDate
      ? allRelevantMonths.filter(month => month.days.some(day => day.checkIn <= calendarCutoffDate))
      : allRelevantMonths
    if (fetching) {
      // setup placeholder months when we're fetching
      // Clamp the placeholder count to the same cutoff so Flash offers don't show trailing
      // placeholder months that will disappear once the real (clamped) months load in.
      const calendarStartDate = beginningOfToday(propertyTimezone)
      const placeholderMonthCount = calendarCutoffDate
        ? Math.max(1, moment(calendarCutoffDate, ISO_DATE_FORMAT, true).startOf('month').diff(moment(calendarStartDate).startOf('month'), 'months') + 1)
        : 18
      const months = fillArray(Math.min(18, placeholderMonthCount)).map<CalendarPickerMonthView>((monthIncrement) => {
        const thisMonth = moment(addMonths(calendarStartDate, monthIncrement))
        const year = thisMonth.year().toString()
        const monthKey = `${thisMonth.month()}-${thisMonth.year()}`
        const month = thisMonth.format('MMMM')
        return {
          key: monthKey,
          year,
          yearNum: thisMonth.year(),
          monthIndex: thisMonth.month(),
          month,
          loading: true,
          packagedTotal: 0,
          hotelTotal: 0,
          isLuxPlusPrice: false,
          limitedAvailability: false,
          hotelAvailable: true,
          packagedAvailable: true,
          flightCacheActive: true,
          hasPromo: false,
          hasTactical: false,
          hasFlightDeal: false,
          cheapest: false,
          loyalty: {
            availableUpgradeTargetsUniqueKeys: {
              room: undefined,
              package: undefined,
            },
          },
        }
      })
      return {
        months,
        availableCount: months.length,
      }
    } else {
      const monthsByYearAndMonth = groupBy(relevantMonths, month => month.key)
      const maybeMemberPriceMaybePrice = <T>(memberPrice: T, nonMemberPrice: T): [T, boolean] => {
        if (displayMemberPrice && !!memberPrice && (memberPrice < nonMemberPrice)) {
          return [memberPrice, true]
        }
        // If: (i) we're not supposed to show LuxPlus+ prices; (ii) the LuxPlus+ price is $0 (i.e.
        // non-existent); or (iii) the LuxPlus+ price is not actually a better deal than the general
        // price), then return the general price.
        return [nonMemberPrice, false]
      }
      const views = Array.from(monthsByYearAndMonth.values()).map((allMonthViews): CalendarPickerMonthView => {
        let months: Array<App.CalendarMonth> = []
        if (pkg) {
          months = allMonthViews.filter(m => m.cheapestDay?.uniqueKey === pkg.uniqueKey)
        } else if (room) {
          months = allMonthViews.filter(m => m.cheapestDay?.roomId === room.id)
        } else {
          months = allMonthViews
        }
        const cheapestDaysList = cheapestDays ? Object.values(cheapestDays) : []
        const cheapestDayPrice = cheapestDaysList.length ? cheapestDaysList.reduce((prev, curr) => prev < curr ? prev : curr) : null

        let hotelDays = nonNullable(months.map(month => month.cheapestDay))
        if (relevantRoomRateIds) {
          hotelDays = hotelDays.filter(day => relevantRoomRateIds.has(day.roomRateId))
        }
        const cheapestHotelDay = hotelDays.length ? hotelDays.reduce((prev, curr) => maybeMemberPriceMaybePrice(prev.hotelMemberTotal, prev.hotelTotal)[0] < maybeMemberPriceMaybePrice(curr.hotelMemberTotal, curr.hotelTotal)[0] ? prev : curr) : null

        const flightDays = nonNullable(months.map(month => month.cheapestDayWithFlights))
        let [hotelTotal, isLuxPlusPrice] = cheapestHotelDay ? maybeMemberPriceMaybePrice(cheapestHotelDay.hotelMemberTotal, cheapestHotelDay.hotelTotal) : [0, false]

        const hasCheapestDay = cheapestHotelDay ? cheapestHotelDay.hotelTotal == cheapestDayPrice : false

        // We can't inline `flightDaysTotals` because we lose the non-emptiness narrowing of
        // `flightDays` when we map it to something else.
        const flightDaysTotals = flightDays.map(day => maybeMemberPriceMaybePrice(day.packagedMemberTotal, day.packagedTotal))
        let [packagedTotal, isPackagedTotalLuxPlusPrice] = isNonEmptyArray(flightDaysTotals) ? min(flightDaysTotals, ([total, _]) => total) : [0, false]
        if (bundledWithFlights && !months[0]?.flightCacheActive) {
          hotelTotal = 0
          // bundling with flights means we have to have a flight for it to have a price
          // equal package and hotel total means no flight prices!
          if (packagedTotal !== hotelTotal) {
            // `packagedTotal` is all good.
            isLuxPlusPrice = isPackagedTotalLuxPlusPrice
          } else {
            packagedTotal = 0
            isLuxPlusPrice = false
          }
        }
        const daysByDay = groupBy(months.flatMap(m => m.days), d => d.day)
        const daysAvailable = countWhere(daysByDay.values(), days => days.some(d => d.hotelAvailable))

        const hasDealId = months[0]?.days.some(day => !!day.dealId)
        const hasFlightDeal = months[0]?.hasFlightDeal

        const monthWithLoyaltyRoomUpgrade = months.find(month => month.days.find(d => !!d.loyalty.availableUpgradeTargetsUniqueKeys.room))
        const monthWithLoyaltyPackageUpgrade = months.find(month => month.days.find(d => !!d.loyalty.availableUpgradeTargetsUniqueKeys.package))

        return {
          key: allMonthViews[0].days[0].monthKey,
          hotelAvailable: hotelDays.some(d => d.hotelAvailable),
          packagedAvailable: flightDays.some(d => d.flightsAvailable),
          hotelTotal,
          packagedTotal,
          isLuxPlusPrice,
          year: allMonthViews[0].year,
          month: allMonthViews[0].month,
          monthIndex: allMonthViews[0].monthIndex,
          // considered limited availability if only 20% of the days of the month are available
          limitedAvailability: daysAvailable > 0 && daysAvailable <= (allMonthViews[0].days.length * 0.2),
          hasPromo: hasDealId,
          hasTactical: months.some(m => m.days.some(d => d.hasTactical && d.hotelAvailable)),
          hasFlightDeal,
          cheapest: hasCheapestDay,
          loyalty: {
            availableUpgradeTargetsUniqueKeys: {
              room: monthWithLoyaltyRoomUpgrade?.loyalty.availableUpgradeTargetsUniqueKeys.room,
              package: monthWithLoyaltyPackageUpgrade?.loyalty.availableUpgradeTargetsUniqueKeys.package,
            },
          },
          flightCacheActive: months.some(month => month.flightCacheActive),
          yearNum: parseInt(allMonthViews[0].year),
        }
      })

      const availableCalendarMonths = trimArray(views, view => !view.hotelAvailable)

      return {
        months: enableCalendarRecommendations ? views : availableCalendarMonths,
        availableCount: availableCalendarMonths.length,
      }
    }
  },
)

export const getDaysOtherAvailability = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.offer!,
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (_state: App.State, offerState: OfferPageState) => getEffectiveBundledWithFlightsOnly(offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleRoute,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleEnabled,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleCheckoutRange,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (
    _offer,
    relevantDays,
    bundledWithFlightsOnly,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
  ) => {
    const allowedWeekdays = (charterBundleRoute && !charterBundleEnabled)
      ? getAllowedCharterCheckInWeekdays(duration)
      : undefined
    const filteredDays = (charterBundleRoute && !charterBundleEnabled)
      ? applyCharterUnbundledDayRestrictions(relevantDays, duration, charterBundleCheckoutRange?.minCheckOut, charterBundleCheckoutRange?.maxCheckOut, allowedWeekdays)
      : relevantDays
    // now find if there are hotels availble on this day
    return filteredDays.reduce<Record<string, boolean>>((currentObject, day) => {
      if (currentObject[day.checkIn]) {
        return currentObject
      }

      currentObject[day.checkIn] = !!(bundledWithFlightsOnly ? (day.flightsAvailable && day.hotelAvailable) : day.hotelAvailable)

      return currentObject
    }, {})
  },
)

export const getMonthsOtherAvailability = createSelector(
  (state: App.State, offerState: OfferPageState) => offerState.offer!,
  (state: App.State, offerState: OfferPageState) => getRelevantDays(state, offerState),
  (_state: App.State, offerState: OfferPageState) => getEffectiveBundledWithFlightsOnly(offerState),
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleRoute,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleEnabled,
  (_state: App.State, offerState: OfferPageState) => offerState.charterBundleCheckoutRange,
  (_state: App.State, offerState: OfferPageState) => offerState.duration,
  (
    _offer,
    relevantDays,
    bundledWithFlightsOnly,
    charterBundleRoute,
    charterBundleEnabled,
    charterBundleCheckoutRange,
    duration,
  ) => {
    const allowedWeekdays = (charterBundleRoute && !charterBundleEnabled)
      ? getAllowedCharterCheckInWeekdays(duration)
      : undefined
    const filteredDays = (charterBundleRoute && !charterBundleEnabled)
      ? applyCharterUnbundledDayRestrictions(relevantDays, duration, charterBundleCheckoutRange?.minCheckOut, charterBundleCheckoutRange?.maxCheckOut, allowedWeekdays)
      : relevantDays
    // group up the days so we can get the cheapest one
    // now find if there are hotels availble on this day

    return filteredDays.reduce<Record<string, boolean>>((currentObject, day) => {
      if (currentObject[day.monthKey]) {
        return currentObject
      }

      currentObject[day.monthKey] = !!(bundledWithFlightsOnly ? (day.flightsAvailable && day.hotelAvailable) : day.hotelAvailable)

      return currentObject
    }, {})
  },
)

export const areRatesLoading = createSelector(
  (state: App.State, offerState: OfferPageState) => pageRequiresRates(offerState),
  (state: App.State, offerState: OfferPageState) => getCurrentAvailableRates(offerState, state),
  (state: App.State, offerState: OfferPageState) => getCurrentMultiRoomRates(offerState, state),
  (state: App.State, offerState: OfferPageState) => isMultipleRoomBookingActive(offerState, state),
  (requiresRates, rates, multiRoomRates, isMultiRoom) => {
    return requiresRates && (isMultiRoom ? multiRoomRates.some(rate => rate.fetching) : rates.fetching)
  })

export const arePricesLoading = createSelector(
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State, offerState: OfferPageState) => offerState.offer!,
  (state: App.State, offerState: OfferPageState) => offerState.rooms,
  (state: App.State, offerState: OfferPageState) => areRatesLoading(state, offerState),
  (state: App.State, offerState: OfferPageState) => !!offerState.checkInDate,
  (state: App.State, offerState: OfferPageState) => getDurationPackageOptions(offerState, state),
  (state: App.State, offerState: OfferPageState) => pageRequiresRates(offerState),
  (state: App.State, offerState: OfferPageState) => isMultipleRoomBookingActive(offerState, state),
  (
    calendarsByPackageKey,
    calendarsByOccupancy,
    offer,
    rooms = initialOfferPageRooms,
    areRatesLoading,
    hasDates,
    durationPackages,
    requiresRates,
    isMultiRoom,
  ) => {
    const requiresCalendarData = !!hasDates
    if (durationPackages.length === 0 && requiresCalendarData) {
      // haven't loaded our packages yet, so we must still be fetching them
      return true
    }

    let calendarReady: boolean = true
    if (isMultiRoom) {
      const occupancyKeys = rooms.map((room) => generateOccupancyStringByRoom(room))
      calendarReady = !!(calendarsByOccupancy[offer.id] || occupancyKeys.every(occupancyKey => durationPackages.every(option => calendarsByOccupancy[occupancyKey]?.[option.package.uniqueKey])))
    } else {
      calendarReady = !!(calendarsByPackageKey[offer.id] || durationPackages.every(option => calendarsByPackageKey[option.package.uniqueKey]))
    }

    const hasAllCalendars = !requiresCalendarData || calendarReady
    const hasAllRates = !requiresRates || !areRatesLoading
    return !(hasAllCalendars && hasAllRates)
  },
)

const getPropertyFlightPriceKey = createSelector(
  (state: OfferPageState) => state.airport?.code,
  (state: OfferPageState) => state.property?.airportCode,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.rooms,
  (
    originAirportCode,
    destinationAirportCode,
    checkIn,
    checkOut,
    duration,
    rooms,
  ) => {
    if (!(originAirportCode && destinationAirportCode && checkIn && checkOut && duration && rooms)) return ''
    const { adults, children, infants } = countOccupantsForFlights(rooms)
    return buildFlightPriceKey({
      numberOfAdults: adults,
      numberOfChildren: children,
      numberOfInfants: infants,
      startDate: checkIn,
      endDate: checkOut,
      nights: duration,
      origin: originAirportCode,
      destination: destinationAirportCode,
    })
  },
)

// TODO(HP-1329): Respect `maxFlightPassengerCount` constant.
const getPropertyCheapestFlightPrice = createSelector(
  (state: OfferPageState) => state.airport?.code,
  (state: OfferPageState) => state.property?.airportCode,
  (state: OfferPageState) => state.checkInDate,
  (_state: OfferPageState, appState: App.State) => appState.flights.flightPricesLoading,
  (_state: OfferPageState, appState: App.State) => appState.flights.flightPrices,
  (state: OfferPageState) => getPropertyFlightPriceKey(state),
  (
    originAirportCode,
    destinationAirportCode,
    checkIn,
    flightPricesLoading,
    flightPrices,
    flightPriceKey,
  ): 'originSameAsDestination' | 'departureTooEarly' | 'departureTooLate' | 'loading' | 'unavailable' | number => {
    if (originAirportCode === destinationAirportCode) return 'originSameAsDestination'
    if (checkIn) {
      // Existing implementations use `moment.utc()` when checking max lead days; I'm not sure if
      // it's strictly necessary.
      const departureDaysInFuture = moment(checkIn).diff(moment(), 'days')
      if (departureDaysInFuture < FLIGHTS_MIN_LEAD_TIME_DAYS) return 'departureTooEarly'
      if (departureDaysInFuture > FLIGHTS_MAX_LEAD_TIME_DAYS) return 'departureTooLate'
    }
    // Bedbank rates also check that fewer than 10 people are travelling. However, it doesn't have
    // special messaging for this case, so we'll ignore it.
    if (flightPricesLoading[flightPriceKey]) return 'loading'
    // Strictly speaking, `getFlightPrice` sets the price for unavailable flights to 0, so this
    // shouldn't normally happen (outside of races). This probably should just be the loading state.
    // On the other hand, we _should_ probably transform a price of 0 to `'unavailable'`.
    if (typeof flightPrices[flightPriceKey] !== 'number') return 'unavailable'
    return flightPrices[flightPriceKey]
  },
)

export function getPropertyOfferMapping(state: OfferPageState, appState: App.State) {
  const offerId = state.offer?.id ?? state.bedbankOffer?.id
  return appState.offer.propertyOfferMappings[offerId ?? '']?.mapping
}

export function isPropertyAvailableForPurchase(_state: OfferPageState, _appState: App.State): boolean {
  // TODO(HP-1495): Impart nuance (e.g. consider schedules).
  return true
}

export function getPropertyFromDurationLabel(state: OfferPageState, _appState: App.State): string {
  const duration = state.duration
  const intl = getCurrentIntl()
  if (duration === 1) {
    return intl.formatMessage({
      id: 'zVO1BmXlPNpi2XNdZ2A9',
      defaultMessage: 'from',
      description: 'Lowercase pricing prefix shown when a one-night property offer omits the duration label',
    })
  }
  return intl.formatMessage({
    id: 'MJ6TqwKcE5MU7JWcXFT5',
    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    description: 'Property offer duration label with from suffix on the offer detail page',
  }, { count: duration })
}

export function getPropertyLoggedOutSavedPercent(_state: OfferPageState, _appState: App.State): number | undefined {
  // TODO(HP-1495): Plumb what to promote through accommodation service property content endpoint.
  return
}

export function getPropertyLowestAvailableStayPayTier(_state: OfferPageState, _appState: App.State): App.StayPayTier | undefined {
  // TODO(HP-1495): Plumb what to promote through accommodation service property content endpoint.
  return
}

export const getPropertyDurationOptions = createSelector(
  (state: OfferPageState) => state.property?.durationOptions,
  (state: OfferPageState) => state.checkInDate,
  (durationOptions, checkIn): Array<OfferDurationOption> => {
    if (!durationOptions) return []
    const checkInDate = checkIn ? new Date(checkIn) : undefined
    return durationOptions.map(({ duration, hasBonusInclusions, hasTactical, promotion }) => {
      const expectedCheckOut = checkInDate ? formatIsoDate(addDays(checkInDate, duration)) : undefined
      return {
        duration,
        hasBonusInclusions,
        hasTactical,
        hasPromotion: (
          !!promotion &&
          (!checkIn || checkIn >= promotion.travelFromIsoDate) &&
          (!expectedCheckOut || expectedCheckOut <= promotion.travelToIsoDate)
        ),
      }
    })
  },
)

const getPropertyRoomsMappings = createSelector(
  (state: OfferPageState) => state.property?.rooms,
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState)?.rooms,
  (propertyRooms, roomMappings) =>
    getRoomsMappings(propertyRooms, roomMappings),
)

export const getPropertyRatesKeyParams = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState)?.propertyId,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.rooms,
  (_state: OfferPageState, appState: App.State) => appState.router.location.query.utm_content,
  (state: OfferPageState) => state.roomOption,
  (propertyId, checkIn, checkOut, occupancies = initialOfferPageRooms, utmContent, roomOption) => ({
    propertyId,
    checkIn,
    checkOut,
    occupancies,
    utmContent,
    roomTypeId: roomOption?.roomTypeId,
    roomRateId: roomOption?.roomRateId,
  }),
)

export const getPropertyRatesKey = createSelector(
  getPropertyRatesKeyParams,
  (params) => buildPropertyRatesKey(params),
)

const EMPTY_ENRICHED_RATE_RELATIONS = {
  packageUpgrade: {
    packageUpgrade: [] as Array<App.EnrichedPropertyRate>,
  },
  paymentMethod: {
    leCollect: [] as Array<App.EnrichedLeCollectPropertyRate>,
    propertyCollect: [] as Array<App.EnrichedPropertyCollectPropertyRate>,
  },
}

function enrichPropertyRooms(
  rooms: ReadonlyArray<App.RawPropertyRoomRates> | undefined,
  cheapestFlightPrice: NonNullable<App.EnrichedPropertyRate['cheapestFlightPrice']>,
  shouldApplyQuickFilters: boolean,
  quickFilters: Partial<OfferPageQuickFilters> | undefined,
): Array<App.EnrichedPropertyRoomRates> {
  if (!rooms?.length) return EmptyArray
  return rooms.map(unfilteredRoom => {
    const room = shouldApplyQuickFilters ? filterRoomRates(unfilteredRoom, quickFilters) : unfilteredRoom
    if ('unavailableReason' in room) return room

    const hotelOnlyRates: Record<string, App.EnrichedPropertyRate> = {}
    const hotelWithFlightsRates: Record<string, App.EnrichedPropertyRate> = {}
    for (const rawRate of Object.values(room.rates)) {
      // Are we allowed to sell the rate without flights?
      if (!rawRate.isFlightBundle) {
        hotelOnlyRates[rawRate.rateId] = {
          ...rawRate,
          /* Metadata */
          defaultOrder: -1,
          orderReason: undefined,
          calloutReason: undefined,
          relations: EMPTY_ENRICHED_RATE_RELATIONS,
          /* TODO: Audit, document, tighten contract */
          includesFlights: false,
          includedGuestsExceededPolicy: undefined,
        }
      }

      // Are we allowed to sell the rate _with_ flights?
      if (rawRate.paymentMethod.type === 'leCollect') {
        if (typeof cheapestFlightPrice === 'string') {
          hotelWithFlightsRates[rawRate.rateId] = {
            ...rawRate,
            /* Metadata */
            defaultOrder: -1,
            orderReason: undefined,
            calloutReason: undefined,
            relations: EMPTY_ENRICHED_RATE_RELATIONS,
            /* TODO: Audit, document, tighten contract */
            includesFlights: true,
            cheapestFlightPrice,
            includedGuestsExceededPolicy: undefined,
          }
        } else {
          const totals = {
            ...rawRate.totals,
            beforeTaxesFeesPrice: rawRate.totals.beforeTaxesFeesPrice + cheapestFlightPrice,
            sellingPrice: rawRate.totals.sellingPrice + cheapestFlightPrice,
            displayPrice: rawRate.totals.displayPrice + cheapestFlightPrice,
            valuedUpToPrice: rawRate.totals.valuedUpToPrice + cheapestFlightPrice,
          }
          totals.discount = getValueOffPercent(totals.valuedUpToPrice, totals.displayPrice)
          let luxPlusTotals: App.PropertyRateTotals | undefined = undefined
          if (rawRate.luxPlusTotals) {
            luxPlusTotals = {
              ...rawRate.luxPlusTotals,
              beforeTaxesFeesPrice: rawRate.luxPlusTotals.beforeTaxesFeesPrice + cheapestFlightPrice,
              sellingPrice: rawRate.luxPlusTotals.sellingPrice + cheapestFlightPrice,
              displayPrice: rawRate.luxPlusTotals.displayPrice + cheapestFlightPrice,
              valuedUpToPrice: rawRate.luxPlusTotals.valuedUpToPrice + cheapestFlightPrice,
            }
            luxPlusTotals.discount = getValueOffPercent(luxPlusTotals.valuedUpToPrice, luxPlusTotals.displayPrice)
          }
          hotelWithFlightsRates[rawRate.rateId] = {
            ...rawRate,
            /* Pricing */
            totals,
            luxPlusTotals,
            /* Metadata */
            defaultOrder: -1,
            orderReason: undefined,
            calloutReason: undefined,
            relations: EMPTY_ENRICHED_RATE_RELATIONS,
            /* TODO: Audit, document, tighten contract */
            includesFlights: true,
            cheapestFlightPrice,
            includedGuestsExceededPolicy: undefined,
          }
        }
      }
    }
    for (const ratesMap of [hotelOnlyRates, hotelWithFlightsRates]) {
      for (const rate of Object.values(ratesMap)) {
        const rawRate = room.rates[rate.rateId]
        if (!rawRate) continue
        const mappedPackageUpgradeRates = rawRate.relations.packageUpgrade.packageUpgrade.flatMap(relatedRateId => {
          const relatedRate = ratesMap[relatedRateId]
          if (relatedRate) return [relatedRate]
          return []
        })
        const mappedLeCollectRates = rawRate.relations.paymentMethod.leCollect.flatMap(relatedRateId => {
          const relatedRate = ratesMap[relatedRateId]
          if (relatedRate && isLeCollectRate(relatedRate)) return [relatedRate]
          return []
        })
        const mappedPropertyCollectRates = rawRate.relations.paymentMethod.propertyCollect.flatMap(relatedRateId => {
          const relatedRate = ratesMap[relatedRateId]
          if (relatedRate && isPropertyCollectRate(relatedRate)) return [relatedRate]
          return []
        })
        if (!(mappedPackageUpgradeRates.length || mappedLeCollectRates.length || mappedPropertyCollectRates.length)) continue
        rate.relations = {
          packageUpgrade: {
            packageUpgrade: mappedPackageUpgradeRates,
          },
          paymentMethod: {
            leCollect: mappedLeCollectRates,
            propertyCollect: mappedPropertyCollectRates,
          },
        }
      }
    }
    const orderedHotelOnlyRates = room.lists.hotelOnly.flatMap(({ id: rateId, orderReason, calloutReason }, defaultOrder) => {
      const rate = hotelOnlyRates[rateId]
      if (!rate) return [] // This _shouldn't_ happen.
      rate.defaultOrder = defaultOrder
      rate.orderReason = orderReason
      rate.calloutReason = calloutReason
      return [rate]
    })
    const orderedHotelWithFlightsRates = room.lists.hotelWithFlights.flatMap(({ id: rateId, orderReason, calloutReason }, defaultOrder) => {
      const rate = hotelWithFlightsRates[rateId]
      if (!rate) return [] // This _shouldn't_ happen.
      rate.defaultOrder = defaultOrder
      rate.orderReason = orderReason
      rate.calloutReason = calloutReason
      return [rate]
    })

    return {
      roomId: room.roomId,
      hotelOnlyRates,
      hotelWithFlightsRates,
      orderedHotelOnlyRates,
      orderedHotelWithFlightsRates,
    }
  })
}

export const getPropertyRatesByRoom = createSelector(
  getPropertyRatesKeyParams,
  (_state: OfferPageState, appState: App.State) => appState.offer.propertyRates,
  (state: OfferPageState, appState: App.State) => getPropertyCheapestFlightPrice(state, appState),
  (state: OfferPageState) => state.rooms.length <= 1,
  (state: OfferPageState) => state.quickFilters,
  (params, propertyRates, cheapestFlightPrice, shouldApplyQuickFilters, quickFilters): Array<App.EnrichedPropertyRoomRates> => {
    // TODO bake in flight statuses, and check whether it's loading?
    const keys = getPropertyRatesReduxKeys(params)
    if (!keys) return EmptyArray
    return enrichPropertyRooms(propertyRates[keys.combined]?.rooms, cheapestFlightPrice, shouldApplyQuickFilters, quickFilters)
  },
)

export const getPropertyRatesByOccupancy = createSelector(
  getPropertyRatesKeyParams,
  (_state: OfferPageState, appState: App.State) => appState.offer.propertyRates,
  (state: OfferPageState, appState: App.State) => getPropertyCheapestFlightPrice(state, appState),
  (params, propertyRates, cheapestFlightPrice): Record<string, Array<App.EnrichedPropertyRoomRates>> => {
    const keys = getPropertyRatesReduxKeys(params)
    if (!keys) return {}
    // Divide the whole-party flight price evenly across the booking rooms so each room's enriched rate
    // carries only its share and the per-room shares sum back to the whole-party total. Quick filters
    // apply only to the single-room combined view (getPropertyRatesByRoom); this seam serves multi-room.
    const perRoomFlightPrice = typeof cheapestFlightPrice === 'number' && params.occupancies.length > 0
      ? cheapestFlightPrice / params.occupancies.length
      : cheapestFlightPrice
    const result: Record<string, Array<App.EnrichedPropertyRoomRates>> = {}
    for (const [occupancyKey, reduxKey] of Object.entries(keys.byOccupancy)) {
      const rawRooms = propertyRates[reduxKey]?.rooms
      if (!rawRooms?.length) continue
      result[occupancyKey] = enrichPropertyRooms(rawRooms, perRoomFlightPrice, false, undefined)
    }
    return result
  },
)

export const getPropertyHotelOnlyRates = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (ratesByRoom): Array<App.EnrichedPropertyRate> => {
    // Only include rates that are directly listed. Rates that can only be accessed indirectly (such
    // as property collect rates that are only visible through a modal triggered from their
    // counterpart LE collect rate) aren't discoverable enough.
    return ratesByRoom.flatMap(room => 'orderedHotelOnlyRates' in room ? room.orderedHotelOnlyRates : [])
  },
)

const EMPTY_AGENT_HUB_BEDBANK_PACKAGES: Array<{ packageId: string; mx: string }> = []

export const getAgentHubBedbankPackagesForProperty = createSelector(
  getPropertyRatesByRoom,
  (ratesByRoom): Array<{ packageId: string; mx: string }> => {
    if (!config.agentHub.isEnabled) return EMPTY_AGENT_HUB_BEDBANK_PACKAGES
    const byId = new Map<string, { packageId: string; mx: string }>()
    for (const room of ratesByRoom) {
      if (!('orderedHotelOnlyRates' in room)) continue
      const rates = [...room.orderedHotelOnlyRates, ...room.orderedHotelWithFlightsRates]
      for (const rate of rates) {
        if (rate.service.type !== 'bedbank' || !rate.totals.mx) continue
        byId.set(rate.service.rateId, { packageId: rate.service.rateId, mx: rate.totals.mx })
      }
    }
    return byId.size ? Array.from(byId.values()) : EMPTY_AGENT_HUB_BEDBANK_PACKAGES
  },
)

const getPropertyPackageOptionsByRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRoomsMappings(state, appState),
  (propertyRatesKey, [propertyRooms]): Array<RoomPackageOptions> => {
    if (propertyRatesKey) return EmptyArray

    const filteredPropertyRooms = Object.values(propertyRooms).filter(({ shouldHideUnlessHasRates }) => !shouldHideUnlessHasRates)
    const sortedPropertyRooms = sortBy(
      filteredPropertyRooms,
      ({ defaultOrder }) => defaultOrder,
      'asc',
    )
    return sortedPropertyRooms.map(propertyRoom => ({
      roomId: propertyRoom.id,
      propertyRoom,
    }))
  },
)

// Room-type rows for the no-dates ("anytime") state, with empty rates. Mirrors the anytime branch of
// getPropertyCombinedRatesByRoom but without the single-room selection/highlight specifics, so the
// multi-room flow can surface selectable room types before dates are chosen. Returns an empty array
// once dates are set, because getPropertyPackageOptionsByRoom yields no options when a rates key exists.
export const getPropertyNoDatesRoomRows = createSelector(
  getPropertyPackageOptionsByRoom,
  (packageOptionsByRoom): Array<RoomRateViews> => packageOptionsByRoom.map(({ roomId, propertyRoom }) => ({
    roomId,
    propertyRoom,
    availableRooms: undefined,
    rates: EmptyArray,
    allRatesQuickFiltered: false,
    roomFitsCapacity: true,
    datesAvailable: true,
    isSelected: false,
    isHighlighted: false,
    roomRateUnmatched: false,
  })),
)

export const getPropertyCombinedRatesByRoom = createSelector(
  (state: OfferPageState) => state.propertyRoomId,
  (state: OfferPageState) => state.roomOption,
  (state: OfferPageState) => state.flights,
  (state: OfferPageState, appState: App.State) => getPropertyPackageOptionsByRoom(state, appState),
  (_state: OfferPageState, appState: App.State) => appState.router.location.query.utm_content,
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRoomsMappings(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (
    selectedRoomId,
    roomOption,
    flights,
    packageOptionsByRoom,
    utmContent,
    mapping,
    [propertyRooms, lpcRooms, bedbankRooms],
    propertyRatesKey,
    ratesByRoom,
  ): Array<RoomRateViews> => {
    const hasRoomOption = !!roomOption && 'roomRateId' in roomOption && 'roomTypeId' in roomOption
    // For `gha-lpp`, we don't expect the `roomOptionRateId` query param to have a currency suffix.
    const baseRoomRateId = (hasRoomOption && utmContent === 'gha-lpc') ?
      roomOption.roomRateId.split('_')[0] :
      roomOption?.roomRateId

    if (propertyRatesKey) {
      if (!mapping) return EmptyArray
      return getRatesByRoom(mapping, propertyRooms, lpcRooms, bedbankRooms, ratesByRoom, flights, selectedRoomId, roomOption?.roomTypeId, baseRoomRateId)
    }

    // The path below is for when dates are _not_ set (a.k.a. "anytime" rates); note that we don't
    // support room-level anytime rates.
    return packageOptionsByRoom.map(({ roomId, propertyRoom }) => {
      return {
        roomId,
        propertyRoom,
        availableRooms: undefined,
        rates: EmptyArray,
        allRatesQuickFiltered: false,
        roomFitsCapacity: true,
        datesAvailable: true,
        isSelected: roomId === selectedRoomId,
        isHighlighted: hasRoomOption && roomId === roomOption.roomTypeId,
        roomRateUnmatched: false,
      }
    })
  },
)

export function getPropertyCombinedRatesByRoomIfOffer(
  offerState: OfferPageState,
  appState: App.State,
): ReturnType<typeof getPropertyCombinedRatesByRoom> {
  if (!offerState.offer) {
    return EmptyArray as ReturnType<typeof getPropertyCombinedRatesByRoom>
  }
  return getPropertyCombinedRatesByRoom(offerState, appState)
}

export const getPropertyMetasearchPriceAccuracyRate = createSelector(
  getPropertyCombinedRatesByRoom,
  (combinedRatesByRoom): App.EnrichedPropertyRate | undefined => {
    // The first tile Google sees: the GHA-highlighted room if it has rates, else the first room
    // with rates. `isSelected` only tracks the last room the user interacted with (for a helper
    // banner), so it's irrelevant to what's displayed first.
    const highlightedRoom = combinedRatesByRoom.find(({ isHighlighted }) => isHighlighted)
    const firstDisplayedRoom = highlightedRoom?.rates.length ?
      highlightedRoom :
        combinedRatesByRoom.find(({ rates }) => rates.length > 0)

    return getCheapestPropertyRatePreferringBedbank(firstDisplayedRoom?.rates ?? [])
  },
)

// Every rate across all rooms — the candidate set for the closest-to-cached-price match.
export const getPropertyMetasearchPriceAccuracyCandidateRates = createSelector(
  getPropertyCombinedRatesByRoom,
  (combinedRatesByRoom): Array<App.EnrichedPropertyRate> =>
    combinedRatesByRoom.flatMap(({ rates }) => rates),
)

// Same row builder as getPropertyCombinedRatesByRoom, but run once per occupancy against that
// occupancy's own enriched rates, so each occupancy's room set and order follow its availability
// response rather than the same-rate-for-all view. The trailing getRatesByRoom args are unset: the
// deep-link highlight does not apply to multi-room, and per-room selection is resolved downstream.
export const getPropertyRatesByRoomByOccupancy = createSelector(
  (state: OfferPageState) => state.flights,
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRoomsMappings(state, appState),
  getPropertyRatesByOccupancy,
  (
    flights,
    mapping,
    [propertyRooms, lpcRooms, bedbankRooms],
    ratesByOccupancy,
  ): Record<string, Array<RoomRateViews>> => {
    if (!mapping) return {}
    const rowsByOccupancy: Record<string, Array<RoomRateViews>> = {}
    for (const [occupancyKey, ratesByRoom] of Object.entries(ratesByOccupancy)) {
      rowsByOccupancy[occupancyKey] = getRatesByRoom(
        mapping, propertyRooms, lpcRooms, bedbankRooms, ratesByRoom, flights, undefined, undefined, undefined,
      )
    }
    return rowsByOccupancy
  },
)

export function getShouldShowPropertyAnytimeRate(state: OfferPageState, appState: App.State): boolean {
  return (
    // Are we obliged to show an anytime rate?
    !!appState.utm.bp &&
    // Have we fetched the anytime rate?
    !!appState.offer.propertyAnytimeRates[getPropertyOfferMapping(state, appState)?.propertyId ?? '']?.anytimeRate
  )
}

const getPropertyAnytimeRate = createSelector(
  (state: OfferPageState, appState: App.State) => getShouldShowPropertyAnytimeRate(state, appState),
  (state: OfferPageState, appState: App.State) =>
    appState.offer.propertyAnytimeRates[getPropertyOfferMapping(state, appState)?.propertyId ?? '']?.anytimeRate,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.rooms,
  (
    shouldShowPropertyAnytimeRate,
    anytimeRate,
    duration,
    rooms,
  ): App.Property.AnytimeRate | undefined => {
    if (!(shouldShowPropertyAnytimeRate && anytimeRate?.duration === duration)) return
    // Our fetched anytime rate is only valid for the default occupancy. This is an approximation.
    if (rooms.length !== 1) return
    const { adults, teenagers, children, infants } = countOccupants(rooms)
    if (!(adults <= 2 && teenagers === 0 && children === 0 && infants === 0)) return

    return anytimeRate
  },
)

const EMPTY_PROPERTY_CHEAPEST_RATES: [
  App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined,
  App.EnrichedPropertyRate | undefined,
] = [undefined, undefined]

export const getPropertyCheapestRates = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyAnytimeRate(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (
    mapping,
    propertyRatesKey,
    anytimeRate,
    ratesByRoom,
  ): [
    App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined,
    App.EnrichedPropertyRate | undefined,
  ] => {
    if (!mapping) return EMPTY_PROPERTY_CHEAPEST_RATES
    if (!propertyRatesKey) {
      // NB: We don't fetch an anytime flight price, so we can't provide the cheapest hotel with
      // flights anytime rate.
      return [anytimeRate, undefined]
    }
    // Only check rates that are directly listed. Rates that can only be accessed indirectly (such
    // as property collect rates that are only visible through a modal triggered from their
    // counterpart LE collect rate) aren't discoverable enough.
    const hotelOnlyRates = ratesByRoom.flatMap(room => 'orderedHotelOnlyRates' in room ? room.orderedHotelOnlyRates : [])
    const hotelWithFlightsRates = ratesByRoom.flatMap(room => 'orderedHotelWithFlightsRates' in room ? room.orderedHotelWithFlightsRates.filter(rate => typeof rate.cheapestFlightPrice === 'number') : [])
    const cheapestHotelOnlyRate = min(hotelOnlyRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    const cheapestHotelWithFlightsRate = min(hotelWithFlightsRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    return [cheapestHotelOnlyRate, cheapestHotelWithFlightsRate]
  },
)

// Cheapest bedbank hotel-only rate — the rate the GHA metasearch discount is anchored on.
// Ranked by the NON-member price (`totals.displayPrice`), since that's the discount basis across
// the GHA chain; for display, `getPropertyCheapestRates` ranks by the member price instead.
// LPC rates are excluded: the discount can't apply to them, so anchoring on one would suppress it
// even when a discountable bedbank rate exists.
export const getPropertyCheapestHotelOnlyRateForDiscount = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (
    mapping,
    propertyRatesKey,
    ratesByRoom,
  ): App.EnrichedPropertyRate | undefined => {
    if (!mapping || !propertyRatesKey) return undefined
    const bedbankHotelOnlyRates = ratesByRoom
      .flatMap(room => 'orderedHotelOnlyRates' in room ? room.orderedHotelOnlyRates : [])
      .filter(rate => rate.service.type === 'bedbank')
    return min(bedbankHotelOnlyRates, rate => rate.totals.displayPrice)
  },
)

const getPropertyAnytimePromotionRate = createSelector(
  (state: OfferPageState, appState: App.State) =>
    appState.offer.propertyAnytimeRates[getPropertyOfferMapping(state, appState)?.propertyId ?? '']?.anytimePromotionRate,
  (state: OfferPageState) => state.duration,
  (state: OfferPageState) => state.rooms,
  (
    anytimePromotionRate,
    duration,
    rooms,
  ): App.Property.AnytimePromotionRate | undefined => {
    // To keep things simple, these guards mirror `getPropertyAnytimeRate`. This way, we don't have
    // to generate a fake anytime promotion rate when customers vary occupancies and length of stay.
    // We'd much rather they just set dates and get a real price.
    if (anytimePromotionRate?.duration !== duration) return
    if (rooms.length !== 1) return
    const { adults, teenagers, children, infants } = countOccupants(rooms)
    if (!(adults <= 2 && teenagers === 0 && children === 0 && infants === 0)) return

    return anytimePromotionRate
  },
)

const EMPTY_PROPERTY_CHEAPEST_PROMOTION_RATES: [
  App.EnrichedPromotionPropertyRate | App.Property.AnytimePromotionRate | undefined,
  App.EnrichedPromotionPropertyRate | undefined,
] = [undefined, undefined]

export const getPropertyCheapestPromotionRates = createSelector(
  (state: OfferPageState, appState: App.State) => getPropertyOfferMapping(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesKey(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyAnytimePromotionRate(state, appState),
  (state: OfferPageState, appState: App.State) => getPropertyRatesByRoom(state, appState),
  (
    mapping,
    propertyRatesKey,
    anytimePromotionRate,
    ratesByRoom,
  ): [
      App.EnrichedPromotionPropertyRate | App.Property.AnytimePromotionRate | undefined,
      App.EnrichedPromotionPropertyRate | undefined,
  ] => {
    if (!mapping) return EMPTY_PROPERTY_CHEAPEST_PROMOTION_RATES
    if (!propertyRatesKey) {
      // NB: We don't fetch an anytime flight price, so we can't provide the cheapest hotel with
      // flights anytime promotion rate.
      return [anytimePromotionRate, undefined]
    }
    // Only check rates that are directly listed. Rates that can only be accessed indirectly (such
    // as through a modal) aren't discoverable enough.
    const hotelOnlyPromotionRates = ratesByRoom.flatMap(room => 'orderedHotelOnlyRates' in room
      ? room.orderedHotelOnlyRates.filter(isPromotionRate)
      : [],
    )
    const hotelWithFlightsPromotionRates = ratesByRoom.flatMap(room => 'orderedHotelWithFlightsRates' in room
      ? room.orderedHotelWithFlightsRates.filter(isPromotionRate).filter(rate => typeof rate.cheapestFlightPrice === 'number')
      : [],
    )
    const cheapestHotelOnlyPromotionRate = min(hotelOnlyPromotionRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    const cheapestHotelWithFlightsPromotionRate = min(hotelWithFlightsPromotionRates, rate => rate.luxPlusTotals?.displayPrice ?? rate.totals.displayPrice)
    return [cheapestHotelOnlyPromotionRate, cheapestHotelWithFlightsPromotionRate]
  },
)

export function arePropertyRatesLoading(state: OfferPageState, appState: App.State): boolean {
  const propertyRatesKey = getPropertyRatesKey(state, appState)
  if (propertyRatesKey) {
    return appState.offer.propertyRates[propertyRatesKey]?.fetching ?? true
  }

  return false
}

export const getFromDurationLabel = createSelector(
  (state: OfferPageState) => state.offer,
  getAvailablePackageOptions,
  getCheapestAvailablePackageOption,
  (state: OfferPageState, appState: App.State) => getCheapestSelectedPackageOption(appState, state),
  (state: OfferPageState) => state.duration,
  (offer, availableOptions, cheapestAvailableOption, cheapestSelectedOption, duration): string => {
    const intl = getCurrentIntl()
    // coming from search page or has selectdd duration in offer page
    if (duration) {
      if (duration === 1) {
        // Show no duration label when starts with a 1 night, only the from
        return intl.formatMessage({
          id: 'zVO1BmXlPNpi2XNdZ2A9',
          defaultMessage: 'from',
          description: 'Lowercase pricing prefix shown when a one-night property offer omits the duration label',
        })
      }
      return intl.formatMessage({
        id: 'CQgcIjB7v8xWyEQUr9x4',
        defaultMessage: '{count, plural, one {# night} other {# nights}} from',
        description: 'Offer page duration label with from suffix on the offer page',
      }, { count: duration })
    }

    if (cheapestSelectedOption) {
      return intl.formatMessage({
        id: '_R6FjIacHcq9eIDVVDjU',
        defaultMessage: '{count, plural, one {# night} other {# nights}} from',
        description: 'Offer page cheapest selected option duration label',
      }, { count: cheapestSelectedOption.package.duration })
    } else if (offer?.lowestPricePackage?.duration === cheapestAvailableOption.package.duration) {
      if (offer.lowestPricePackage.duration === 1) {
        // Show no duration label when starts with a 1 night, only the from
        return intl.formatMessage({
          id: 'zVO1BmXlPNpi2XNdZ2A9',
          defaultMessage: 'from',
          description: 'Lowercase pricing prefix shown when a one-night property offer omits the duration label',
        })
      }
      return intl.formatMessage(
        {
          id: '9joyWDXdeWneikc6Hzt4',
          defaultMessage: '{durationLabel} from',
          description: 'Offer page duration label from the offer data followed by the pricing prefix',
        },
        { durationLabel: offer.durationLabel },
      )
    } else {
      const durations = new Set(availableOptions.map(view => view.package.duration))
      if (durations.has(1)) {
        // Show no duration label when starts with a 1 night, only the from
        return intl.formatMessage({
          id: 'zVO1BmXlPNpi2XNdZ2A9',
          defaultMessage: 'from',
          description: 'Lowercase pricing prefix shown when a one-night property offer omits the duration label',
        })
      }
      return intl.formatMessage(
        {
          id: 'k3uZ7hyTZDRFxYay591l',
          defaultMessage: '{durationLabel} from',
          description: 'Offer page generated duration label followed by the pricing prefix',
        },
        { durationLabel: getDurationLabel(Array.from(durations), intl) },
      )
    }
  },
)

export const getPropertyIdIfBestOfferRedirectRequired = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (offer) => {
    if (offer?.property) {
      return offer.property.id
    }
  },
)

export const getPropertyAlternateOfferIds = createSelector(
  (state: App.State, offerId: string) => (state.offer.offers[offerId] || state.offer.offerSummaries[offerId])?.property?.id,
  (state: App.State) => state.offer.alternativeOffersForProperty,
  (propertyId, alternativeOffersForProperty) => {
    if (propertyId) {
      return alternativeOffersForProperty[propertyId]
    }
  },
)

export const getPropertyBestAlternateOfferIds = createSelector(
  (state: App.State, offerId: string) => getPropertyIdIfBestOfferRedirectRequired(state, offerId),
  (state: App.State) => state.offer.alternativeOffersForProperty,
  (propertyId, alternativeOffersForProperty) => {
    if (propertyId) {
      return alternativeOffersForProperty[propertyId]
    }
  },
)

export const getPropertyAlternateOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.bedbankOffers,
  (alternatePropertyOffers, offers, bedbankOffers) => {
    if (alternatePropertyOffers) {
      if (alternatePropertyOffers.le.length > 0) {
        return offers[alternatePropertyOffers.le[0]]
      } else if (alternatePropertyOffers.bedbank.length > 0) {
        return bedbankOffers[alternatePropertyOffers.bedbank[0]]
      }
    }
  },
)

export const getPropertyBestAlternateOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.offers,
  (state: App.State, offerId: string) => offerId,
  (alternatePropertyOffers, offers, offerId) => {
    if (alternatePropertyOffers?.le && alternatePropertyOffers.le.length > 0 && offers[offerId].type !== 'hotel') {
      const alt = alternatePropertyOffers.le.find((offer: string) => offer !== offerId)
      if (alt && offers[offerId].type !== offers[alt]?.type) {
        return offers[alt]
      }
    }
  },
)

export const getPropertyBedbankOffer = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOfferIds(state, offerId),
  (state: App.State) => state.offer.bedbankOffers,
  (alternatePropertyOffers, bedbankOffers) => {
    const alternateOfferId = alternatePropertyOffers?.bedbank?.[0]
    if (alternateOfferId) {
      return bedbankOffers[alternateOfferId]
    }
  },
)

export const getPropertyAlternateURL = createSelector(
  (state: App.State, offerId: string) => getPropertyAlternateOffer(state, offerId),
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode) => {
    if (offer) {
      if (offer.type === 'bedbank_hotel') {
        return `/${regionCode.toLowerCase()}/partner/${offer.slug}/${offer.id}`
      } else {
        return `/${regionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`
      }
    }
  },
)

export const getPropertyBestAlternateURL = createSelector(
  (state: App.State, offerId: string) => getPropertyBestAlternateOffer(state, offerId),
  (state: App.State) => state.geo.currentRegionCode,
  (offer, regionCode) => {
    if (offer) {
      return `/${regionCode.toLowerCase()}/offer/${offer.slug}/${offer.id}`
    }
  },
)

export const isAvailableForPurchase = createSelector(
  (state: OfferPageState) => state.offer!,
  (state: OfferPageState, appState: App.State) => getPackageViews(state, appState),
  (_state: OfferPageState, appState: App.State) => appState.system.previewMode,
  (offer, views, previewMode) => {
    if (previewMode) {
      return true
    }

    const hotelAvailable = views.some(p => p.hotelAvailable)

    if (!hotelAvailable) {
      return false
    }

    // NB: As of 2026-02-27, we no longer gate LE users against list visibility schedules. This also
    // means we no longer gate against LuxPlus+ schedules, as we assume online purchase schedules
    // encompass both list visibility and LuxPlus+ schedules. For further context, see
    // <https://luxgroup-hq.slack.com/archives/C02CJFUT17V/p1772071816827949>.
    return scheduleIsCurrent(offer.onlinePurchaseSchedule)
  },
)

export const isBNBLAvailable = createSelector(
  (state: OfferPageState) => state.offer!,
  (offer) => offer.hasBuyNowBookLaterPackages &&
    offer.hasBuyNowBookLater &&
    !!offer.travelToDate &&
    config.accommodation.hasBnbl,
)

export const isFetchingCalendars = createSelector(
  (appState: App.State) => appState.calendar.calendarsByPackageKey,
  (appState: App.State, pageState: OfferPageState) => getRelevantPackages(pageState, appState),
  (calendars, packages) => {
    return packages.some(pkg => !calendars[pkg.package.uniqueKey])
  },
)

export const getSelectedPackageView = createSelector(
  (state: OfferPageState) => state.selectedPackageKey,
  getDurationPackageOptions,
  (selectedPackageKey, options) => {
    if (selectedPackageKey) {
      return options.find(option => option.package.uniqueKey === selectedPackageKey)
    }
  },
)

/**
 * Whether or not there's enough information for an offer page to be able to continue to checkout
 */
export const canBookNow = createSelector(
  (state: OfferPageState, appState: App.State) => getSelectedOfferPageDay(appState, state),
  (state: OfferPageState) => state.rooms,
  getSelectedPackageView,
  (state: OfferPageState) => state.offer,
  (selectedDay, rooms = [], selectedPackageView, offer) => {
    const hasValidDateAndRooms = !!selectedDay && hasSelectedRooms(rooms) && !isRoomsInvalid(rooms)
    if (isVillaOffer(offer)) {
      // villas don't have multiple packages, so dont' require a 'selected package view' - they are always selected
      return hasValidDateAndRooms && selectedDay.hotelAvailable
    }
    return hasValidDateAndRooms && !!selectedPackageView && !offer?.property?.useDynamicCancellationPolicies && selectedPackageView.hotelAvailable
  },
)

export const markCheapestPrice = createSelector(
  (state: OfferPageState) => state.offer?.type,
  (offerType) => {
    return offerType && [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL].includes(offerType)
  },
)

export const getCheckoutDate = createSelector(
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.duration,
  (checkIn, checkOut, duration) => {
    if (checkOut) {
      return checkOut
    }
    if (duration) {
      return checkIn ? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined
    }
  },
)

/**
 * Whether or not flights are enabled for the current offer page
 */
export const offerFlightsEnabled = createSelector(
  (state: OfferPageState) => state.offer,
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState, appState: App.State) => appState.geo.currentRegionHasFlights,
  (state: OfferPageState) => state.charterBundleRoute,
  (state: OfferPageState) => state.charterBundleEnabled,
  (offer, bedbankOffer, rooms, regionHasFlights, charterBundleRoute, charterBundleEnabled): boolean => {
    if (bedbankOffer) {
      const occupancyCount = sumUpOccupancies(rooms)
      return !!(bedbankOffer.flights.flightsEnabled &&
        config.BEDBANK_FLIGHT_BOOKING_ENABLED &&
        regionHasFlights &&
        occupancyCount < 10)
    } else if (offer) {
      if (charterBundleRoute && !charterBundleEnabled) {
        return false
      }
      return !!(regionHasFlights && offer?.offerFlightsEnabled)
    }
    return false
  },
)

const defaultRooms = [generateOfferPageRoom()]

export const getCurrentFullOccupancyLabel = createSelector(
  (offerPageState: OfferPageState) => offerPageState.offer! || offerPageState.bedbankOffer!,
  (offerPageState: OfferPageState) => offerPageState.rooms,
  (offer, rooms = defaultRooms) => {
    const intl = getCurrentIntl()
    let text = formatOccupantsShort(rooms)
    if (!!offer.saleUnit && !!rooms.length) {
      text = intl.formatMessage(
        {
          id: 'zD_79jX_Unmxli9_avzg',
          defaultMessage: '{occupancyText} · {saleUnitLabel}',
          description: 'Offer page full occupancy label with sale unit count',
        },
        {
          occupancyText: text,
          saleUnitLabel: formatSaleUnitCount(intl, offer.saleUnit, rooms.length),
        },
      )
    }
    return text
  },
)

export const getCurrentShortOccupancyLabel = createSelector(
  (offerPageState: OfferPageState) => offerPageState.offer! || offerPageState.bedbankOffer!,
  (offerPageState: OfferPageState) => offerPageState.rooms,
  (offer, rooms = defaultRooms) => {
    const intl = getCurrentIntl()
    const { adults, teenagers, children, infants } = countOccupants(rooms)
    const personLabel = children || infants ? 'guest' : 'adult'
    const occupantCount = adults + teenagers + children + infants
    let text = personLabel === 'guest' ? intl.formatMessage(
      {
        id: 'MurBazqSTQasNwtlFdbd',
        defaultMessage: '{count, plural, one {# guest} other {# guests}}',
        description: 'Short occupancy label showing guest count on offer page',
      },
      { count: occupantCount },
    ) : intl.formatMessage(
      {
        id: 'QrTPEmVRo9I7frYrpSX1',
        defaultMessage: '{count, plural, one {# adult} other {# adults}}',
        description: 'Short occupancy label showing adult count on offer page',
      },
      { count: occupantCount },
    )
    if (!!offer.saleUnit && !!rooms.length) {
      text = intl.formatMessage(
        {
          id: 'lNP2dk5CigCa-eiQKVFs',
          defaultMessage: '{occupancyText} · {saleUnitLabel}',
          description: 'Offer page short occupancy label with sale unit count',
        },
        {
          occupancyText: text,
          saleUnitLabel: formatSaleUnitCount(intl, offer.saleUnit, rooms.length),
        },
      )
    }
    return text
  },
)

export const getIsOccupancyMissingChildAges = createSelector(
  (offerPageState: OfferPageState) => offerPageState.rooms,
  (rooms) => {
    return rooms.some(room => room.childrenAge?.some(age => age === -1))
  },
)

export const getCurrentDateLabel = createSelector(
  (offerPageState: OfferPageState) => offerPageState.checkInDate,
  (offerPageState: OfferPageState) => offerPageState.checkOutDate,
  (offerPageState: OfferPageState) => offerPageState.duration,
  (offerPageState: OfferPageState) => getEffectiveCharterBundleRoute(offerPageState),
  (checkIn, checkOut, duration, charterBundleRoute) => {
    // For charter flights, show flight dates (departure is day before check-in, return is checkout day)
    if (charterBundleRoute && checkIn) {
      const flightDepartDate = moment(checkIn).subtract(1, 'day').format(ISO_DATE_FORMAT)
      const flightReturnDate = checkOut ?? moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
      return formatDynamicDateRange(flightDepartDate, flightReturnDate)
    }
    return formatDynamicDateRange(checkIn, checkOut, duration)
  },
)

export const getDatesOccupancyLabel = createSelector(
  getCurrentDateLabel,
  getCurrentFullOccupancyLabel,
  (dateLabel, occupancyLabel) => {
    return `${dateLabel}, ${occupancyLabel}`
  },
)

export const getCanUseMultiRoomUI = (offerState: OfferPageState, appState: App.State) => getIsMultiRoomEnabled(appState, offerState.offer) && offerState.rooms.length > 1

export const getHasSoloCapacityPackages = createSelector(
  (state: OfferPageState, appState: App.State) => getBasePackageViews(state, appState),
  (packageViews) => packageViews.some(view => hasSoloCapacity(view.package)),
)

export const getOfferDestinationAirport = createSelector(
  (_offerState: OfferPageState, appState: App.State) => appState.geo.airports,
  (offerState: OfferPageState, _appState: App.State) => offerState.offer?.flightDestinationPort,
  (airports, flightDestinationPort) => airports.find(({ code }) => code === flightDestinationPort),
)

/**
 * Resolves the room rate ID for the session.
 * Priority: explicitly selected room option → package matched by selectedPackageKey → lowest price package fallback.
 */
export function getSelectedRoomRateId(state: OfferPageState): string | undefined {
  return state.roomOption?.roomRateId ??
    (state.offer as App.HotelOffer | undefined)?.packages
      ?.find(pkg => pkg.uniqueKey === state.selectedPackageKey)?.roomRate?.id ??
      (state.offer as App.HotelOffer | undefined)?.lowestPricePackage?.roomRate?.id
}
